import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AddRounded from "@mui/icons-material/AddRounded";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import MailOutline from "@mui/icons-material/MailOutline";
import NotesRounded from "@mui/icons-material/NotesRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import {
  useCorrespondentsApi,
  docId,
  oidOf,
  CONTACT_TYPES,
} from "../../../hooks/useCorrespondentsHook";
import { useEnsureUsers } from "../../../hooks/useEnsureReferenceData";
import {
  useCachedLoanPipeline,
  filterLoansByOriginator,
} from "../../../hooks/useCachedLoanPipeline";
import CorrespondentLoansTab from "../components/CorrespondentLoansTab";
import CorrespondentOnboardingTab from "../components/CorrespondentOnboardingTab";
import CorrespondentProfileTab from "../components/CorrespondentProfileTab";
import CorrespondentAdminTab from "../components/CorrespondentAdminTab";
import CorrespondentDocumentsCoverage from "../components/CorrespondentDocumentsCoverage";
import FundingEntitiesEditor from "../components/FundingEntitiesEditor";
import {
  CorrespondentContactPanel,
  CorrespondentOverlayPanel,
} from "../components/CorrespondentPanelForms";
import PendingChangesBar from "../../../components/common/PendingChangesBar";
import {
  // Every PricingField in this file is on the Pricing tab's cards, which use
  // the field label / helper standard (FIELD_TEXT) — hence the alias.
  PricingFieldText as PricingField,
  PricingPageLayout,
  T as PT,
} from "../../admin/AdminPricingScreen/pricingUi";
import { FIELD_TEXT } from "../../../theme/formStyles";
import {
  Cell,
  FIELD_SX,
  Grid,
  ProfileSection,
} from "../components/correspondentForm";
import BlockNoteEditor from "../../../components/html/BlockNoteEditor";
import Flx from "../../../components/layout/Flx";
import { alpha } from "@mui/material/styles";

import { teal, status as statusColors } from "../../../theme/colors";
import { partnerStorage } from "../../../utils/storage/partnerStorage";
import { T } from "../../SupportScreen/components/supportUi";
import { TierChip } from "../components/TierChip";
import {
  OnboardingStatusChip,
  SoftBadge,
  lastPricingChangeOf,
  pricingChangeFields,
} from "../components/correspondentUi";
import FlxGrid from "../../../_src_shared/components/layout/FlxGrid";
import { formatDate as sharedFormatDate } from "../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";
import { formatPhone } from "../../../_src_shared/utils/functions/formatPhone";

/* ----------------------------- tokens / helpers ---------------------------- */

// Global DSCR tier margins (bond points). The runtime source of truth is the
// backend dscr_tier_margins collection (fetched in PricingTab); this in-code
// mirror is only the fallback before the collection is seeded.
const TIER_MARGINS = { Gold: 0.5, Silver: 0.75, Bronze: 1.0 };

// RTL products that carry a correspondent-specific spread (rtl_pricing_spreads
// map). Mirrors the backend RTL_PRODUCTS taxonomy; the live list is fetched in
// PricingTab so new products appear automatically. Heavy Rehab is a secondary of
// Fix and Flip and prices off its own row/spread.
const RTL_SPREAD_PRODUCTS = [
  { key: "fix_and_flip", label: "Fix and Flip" },
  {
    key: "fix_and_flip_heavy_rehab",
    label: "Fix and Flip — Heavy Rehab",
    secondary_of: "fix_and_flip",
  },
  { key: "ground_up_construction", label: "Ground Up Construction" },
  { key: "bridge", label: "Bridge" },
];

// rtl_pricing_spreads are stored as decimal fractions (0.01 = 1.00%); the editor
// edits in percent strings (avoids controlled-input rounding while typing) and
// converts on save.
const spreadDecToPct = (m) => {
  const out = {};
  Object.entries(m || {}).forEach(([k, v]) => {
    out[k] = v == null || v === "" ? "" : String(+(Number(v) * 100).toFixed(4));
  });
  return out;
};
const spreadPctToDec = (m) => {
  const out = {};
  Object.entries(m || {}).forEach(([k, v]) => {
    if (v === "" || v == null) return;
    const n = Number(v);
    if (!Number.isNaN(n)) out[k] = Number((n / 100).toFixed(6));
  });
  return out;
};

// Content max-width matches the Users screen (<Container maxWidth="xl"> = 1536px).
const MAX_W = 1536;

// FREE_DOMAINS moved with the email-domain editor into
// components/CorrespondentAdminTab.js.

const trimZeros = (s) => s.replace(/0+$/, "").replace(/\.$/, "");
const fmtPts = (n) => `${trimZeros(Number(n || 0).toFixed(3))} pts`;
// RTL/GUC account spreads on the Pricing tab are PERCENT of an annual rate
// (0.25 = 0.25%) — the tier spread, the overlay and the effective spread. The
// per-product override map stays a decimal fraction on the model and is
// converted for display. `signed` prefixes + / − so an overlay reads as a
// premium or a discount.
const fmtPct = (n, { signed } = {}) => {
  const v = Number(n || 0);
  const abs = `${trimZeros(Math.abs(v).toFixed(3)) || "0"}%`;
  if (signed) return v < 0 ? `−${abs}` : `+${abs}`;
  return v < 0 ? `-${abs}` : abs;
};
// A tier still at the seed's 0 carrying the seed note has not been priced by
// the desk — it reads "not yet priced", never as a free 0% spread.
const RTL_TIER_SEED_NOTE = "seed - desk sets values";
const rtlTierUnpriced = (row) =>
  !!row &&
  Number(row.spreadPct || 0) === 0 &&
  String(row.notes || "").trim() === RTL_TIER_SEED_NOTE;
const fmtMoney = (n) => (n ? `$${(n / 1000000).toFixed(1)}M` : "—");

const localPart = (email) => (email ? String(email).split("@")[0] : null);

const contactName = (c) => {
  if (!c) return "";
  const full = `${c.firstName || ""} ${c.lastName || ""}`.trim();
  return full || c.fullName || c.emailAddress || "—";
};
const contactInitials = (c) => {
  const n = contactName(c);
  const parts = String(n).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const totalVolume = (d) =>
  (d?.onboardingEstimateRTLVolume || 0) +
  (d?.onboardingEstimateDSCRVolume || 0) +
  (d?.onboardingEstimateGroundUpVolume || 0);

const fmtDate = (v) => {
  if (!v) return "—";
  return sharedFormatDate(v, String(v), "MMM d, yyyy");
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    background: "#fff",
  },
};

/* ------------------------------ small pieces ------------------------------- */

function SectionCard({ title, icon: Icon, action, children, sx, bodySx }) {
  return (
    <Box
      component="section"
      sx={{
        borderRadius: "12px",
        border: `1px solid ${T.line}`,
        background: "#fff",
        boxShadow: T.shadow,
        // A grid/flex item defaults to `min-width: auto`, so a long unbroken
        // string (a URL, an email, a diff line) widens the card instead of
        // wrapping inside it. minWidth:0 lets the card shrink to its track.
        minWidth: 0,
        maxWidth: "100%",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: `1px solid ${T.line}`,
          px: 2.5,
          py: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: "12px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: T.ink500,
          }}
        >
          {Icon && <Icon sx={{ fontSize: 15, color: T.ink300 }} />}
          {title}
        </Box>
        {action}
      </Box>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          minWidth: 0,
          // Prose in these cards is long — make sure it wraps rather than
          // running past the card edge.
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          // A card whose body IS a table/grid passes bodySx={{ p: 0 }} so the
          // grid sits flush against the card edges.
          ...bodySx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function FieldRow({ label, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        py: 0.75,
      }}
    >
      <Box sx={{ fontSize: "13px", color: T.ink300, flexShrink: 0 }}>
        {label}
      </Box>
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: T.ink700,
          textAlign: "right",
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Label({ children }) {
  return (
    <Box
      sx={{
        mb: 0.5,
        fontSize: "10.5px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: T.ink300,
      }}
    >
      {children}
    </Box>
  );
}

const MlpaBadge = ({ signed }) =>
  signed ? (
    <SoftBadge tone="green" icon={CheckRounded} outlined>
      MLPA signed
    </SoftBadge>
  ) : (
    <SoftBadge tone="amber" outlined>
      MLPA pending
    </SoftBadge>
  );

/* ----------------------------- detail panel -------------------------------- */

// Verbatim from the backend `SERVICER_OPTIONS` (app/models/correspondents.py) —
// matched by string equality server-side, so don't reword.
//
// These CANNOT come from `useModelFieldOptions`: `GET /model_schemas/object/
// {object_type}` only serves borrower / subjectProperty / loanBaseModel /
// underwriting / entity / address / unit. `correspondent_company` appears only
// in the `base_models_df` xlsx dump, so there is no schema read for it — the
// enums below are copied by hand and must be re-checked when the model changes.
const SERVICER_OPTIONS = ["FCI", "Shellpoint", "Other"];

// `CorrespondentServiceProvider.provider` — third-party services a correspondent
// is onboarded to BEYOND the servicer (TrustPoint = draw management).
const SERVICE_PROVIDER_OPTIONS = ["TrustPoint", "Other"];

// `SERVICE_PROVIDER_STATUS_OPTIONS`. "Attention" means flagged / needs
// follow-up, and the FE doc asks for it to read amber-red rather than neutral.
const SERVICE_PROVIDER_STATUS_OPTIONS = [
  "Onboarded",
  "Email Sent",
  "Not Applicable",
  "Attention",
];

const SERVICE_STATUS_TONE = {
  Onboarded: "green",
  "Email Sent": "steel",
  "Not Applicable": "slate",
  Attention: "amber",
};

const TABS = [
  "Profile",
  "Onboarding Checklist",
  "Contacts",
  "Coverage",
  "Servicing",
  "Funding Entities",
  "Pricing",
  "Overlays",
  "Agreements",
  "Notes",
  "Users",
  "Loans",
  "Admin",
];

export function CorrespondentDetailPanel({
  id,
  showBackLink = false,
  onClose,
  onDark = false,
  onDeleted,
}) {
  // The Coverage tab's email picker resolves against the shared `users` slice —
  // pull it lazily (cache-gated) instead of relying on a global eager fetch.
  useEnsureUsers();
  const correspondentsApi = useCorrespondentsApi();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("Profile");
  // An unsaved Notes draft is kept in this browser; the Notes tab label says so.
  const [notesDirty, setNotesDirty] = useState(() => !!(id && readNoteDraft(id)));
  useEffect(() => setNotesDirty(!!(id && readNoteDraft(id))), [id]);
  // Onboarding checklist progress for the tab pill — fetched with the record so
  // it shows before the tab is opened, and pushed back up by the tab on save.
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const isModal = typeof onClose === "function";

  const refresh = () => {
    if (!id) return;
    correspondentsApi.get(id).then(setDetail);
    correspondentsApi
      .getOnboardingChecklist(id)
      .then((v) => setOnboardingProgress(v?.progress ?? null))
      .catch(() => setOnboardingProgress(null));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Loans come from GET /correspondents/{id}/loans — both pipelines (pre-close
  // + acquisitions), matched on the loan's link to this record, each row with
  // its own `link` / `outcome` / `keyDate`. Fetched per correspondent (never
  // cached) so new trades show up. Until that route is deployed the call fails
  // and the tab keeps the old behaviour: the cached pre-close list matched by
  // originator name. Resolved here so the tab LABEL carries the count.
  const [loanFeed, setLoanFeed] = useState({ status: "loading", rows: [], total: null });
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setLoanFeed({ status: "loading", rows: [], total: null });
    correspondentsApi
      .loans(id)
      .then((res) => {
        if (!alive) return;
        const rows = Array.isArray(res?.loans) ? res.loans : [];
        setLoanFeed({ status: "ok", rows, total: res?.counts?.total ?? rows.length });
      })
      .catch(() => alive && setLoanFeed({ status: "fallback", rows: [], total: null }));
    return () => {
      alive = false;
    };
  }, [id, correspondentsApi]);
  const useFallback = loanFeed.status === "fallback";
  const { pipeline, isLoaded: pipelineLoaded } = useCachedLoanPipeline({
    auto: useFallback,
  });
  const fallbackRows = useMemo(
    () => (useFallback ? filterLoansByOriginator(pipeline, detail?.companyName) : []),
    [useFallback, pipeline, detail?.companyName],
  );
  const loanRows = useFallback ? fallbackRows : loanFeed.rows;
  const loansLoading = loanFeed.status === "loading" || (useFallback && !pipelineLoaded);

  const patch = async (body) => {
    await correspondentsApi.update(id, body);
    refresh();
  };

  // Centered spinner while the record loads — full page height on the route,
  // a shorter block when embedded in an expanded grid row.
  if (!detail)
    return (
      <Box
        sx={{
          minHeight: showBackLink ? "60vh" : 240,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          color: T.ink300,
          fontSize: 13,
        }}
      >
        <CircularProgress size={28} />
        Loading correspondent…
      </Box>
    );

  // Counts rendered as a pill next to the tab label.
  const tabCounts = {
    Loans: loansLoading ? null : (loanFeed.status === "ok" ? loanFeed.total : loanRows.length),
    "Funding Entities": (detail.fundingEntities ?? []).length,
    // "3/12" required documents done; "✓" once finally signed off.
    "Onboarding Checklist": onboardingProgress
      ? onboardingProgress.finalSignedOff
        ? "✓"
        : `${onboardingProgress.doneItems}/${onboardingProgress.requiredItems}`
      : null,
  };

  const overlays = detail.guidelineOverlays ?? detail.overlays ?? [];
  const stats = [
    ["Overlays", overlays.length],
    ["Email domains", (detail.companyEmailDomains ?? []).length],
    ["Est. volume", fmtMoney(totalVolume(detail))],
  ];

  return (
    <Box
      data-testid="correspondent-drilldown-root"
      sx={{
        bgcolor: onDark ? "#fff" : T.bg,
        borderRadius: 0,
        // Embedded, the panel is flush with the grid row — no inset card, no
        // padding of its own beyond the breathing room the tab bodies carry.
        px: onDark ? 2.5 : 3,
        py: onDark ? 2 : 3,
        minHeight: onDark ? "auto" : "100%",
        // The route container is a fixed-height `display:flex; flex-direction:
        // column` with `overflow-y:auto`. As a flex item this panel would be
        // SHRUNK to the container's height while its content spilled past —
        // which is why the tinted background stopped mid-page and the cards
        // below it sat on bare white. `flexShrink:0` lets the item take its
        // content height, so the background covers everything that scrolls.
        flexShrink: 0,
        // Embedded in an AG Grid detail row the panel must never be wider than
        // the row, and `white-space` must not inherit from `.ag-row`.
        maxWidth: "100%",
        minWidth: 0,
        whiteSpace: "normal",
      }}
    >
      {/* The Loans tab is a wide table — it gets the full width, no cap. */}
      <Box
        sx={{
          width: "100%",
          maxWidth: tab === "Loans" ? "none" : MAX_W,
          mx: "auto",
          minWidth: 0,
        }}
      >
        {/* breadcrumb / close / open-full-page */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            // Nothing above the tabs when the panel is on its own page with no
            // back link and no close button.
            ...(showBackLink || isModal || onDark ? {} : { display: "none" }),
          }}
        >
          {showBackLink && (
            <Button
              variant="text"
              onClick={() => navigate("/correspondents")}
              startIcon={<ChevronLeftRounded />}
              sx={{
                color: T.ink500,
                fontSize: 13,
                fontWeight: 500,
                "&:hover": { color: T.ink900, bgcolor: "transparent" },
              }}
            >
              Correspondents
            </Button>
          )}
          {isModal && (
            <IconButton
              onClick={onClose}
              aria-label="close"
              sx={{ ml: onDark ? 0 : "auto" }}
            >
              <CloseRounded />
            </IconButton>
          )}
        </Box>

        {/* identity */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                background: T.ink900,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <BusinessOutlined sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: T.ink900,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {detail.companyName}
                </Box>
                {/* Status leads the chips: whether this partner is live is the
                    first thing to know about them, ahead of their tier. */}
                <OnboardingStatusChip status={detail.onboardingStatus} outlined />
                <TierChip
                  tier={detail.pricingTier ?? detail.dscr_pricing_tier}
                />
                <MlpaBadge signed={!!detail.mlpaSigned} />
              </Box>
              <Box sx={{ mt: 0.25, fontSize: "13px", color: T.ink500 }}>
                {detail.correspondentRelationship || "—"}
                {addressLabel(detail.companyAddress)
                  ? ` · ${addressLabel(detail.companyAddress)}`
                  : ""}
              </Box>
              {/* An Inactive partner without the WHY is the state this field
                  exists to prevent, so the reason rides the header. */}
              {detail.onboardingStatus === "Inactive" && (
                <Box
                  sx={{
                    mt: 0.5,
                    fontSize: "12.5px",
                    color: statusColors.error.fg,
                    overflowWrap: "anywhere",
                  }}
                >
                  Inactive: {detail.inactiveReason || "no reason recorded"}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3, textAlign: "center" }}>
            {stats.map(([l, n]) => (
              <Box key={l}>
                <Box
                  sx={{ fontSize: "18px", fontWeight: 600, color: T.ink900 }}
                >
                  {n}
                </Box>
                <Box
                  sx={{ fontSize: "11px", fontWeight: 500, color: T.ink300 }}
                >
                  {l}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* tabs */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            mb: 3,
            borderBottom: `1px solid ${T.line}`,
            // overflowX:auto alone makes the browser compute overflowY to auto,
            // which the 1px underline overflow turns into a phantom vertical
            // scrollbar — pin overflowY hidden to suppress it.
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          {TABS.map((t) => {
            const active = tab === t;
            const count = tabCounts[t];
            return (
              <Box
                key={t}
                component="button"
                onClick={() => setTab(t)}
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  px: 1.75,
                  py: 1.25,
                  fontSize: "13.5px",
                  fontWeight: active ? 700 : 500,
                  color: active ? T.teal600 : T.ink500,
                  "&:hover": { color: active ? T.teal600 : T.ink900 },
                }}
              >
                {t}
                {t === "Notes" && notesDirty ? (
                  <Box
                    component="span"
                    title="Unsaved draft"
                    aria-label="Unsaved draft"
                    sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: statusColors.warning.fg }}
                  />
                ) : null}
                {count != null && (
                  <Box
                    component="span"
                    sx={{
                      borderRadius: "999px",
                      px: "6px",
                      minWidth: 18,
                      fontSize: "11px",
                      fontWeight: 700,
                      lineHeight: "17px",
                      background: active ? T.teal50 : "#f1f5f9",
                      color: active ? T.teal600 : T.ink300,
                    }}
                  >
                    {count}
                  </Box>
                )}
                {active && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 8,
                      right: 8,
                      bottom: -1,
                      height: "2.5px",
                      borderRadius: "999px",
                      background: T.teal600,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {tab === "Profile" && (
          <ProfileTab
            detail={detail}
            patch={patch}
            onChanged={refresh}
            onOpenAgreements={() => setTab("Agreements")}
            onDeleted={() => {
              if (typeof onDeleted === "function") onDeleted();
              else navigate("/correspondents");
            }}
          />
        )}
        {tab === "Onboarding Checklist" && (
          <CorrespondentOnboardingTab
            detail={detail}
            onProgress={setOnboardingProgress}
            onChanged={refresh}
            onOpenAgreements={() => setTab("Agreements")}
          />
        )}
        {tab === "Contacts" && (
          <ContactsTab detail={detail} onChanged={refresh} patch={patch} />
        )}
        {tab === "Coverage" && <CoverageTab detail={detail} patch={patch} />}
        {tab === "Servicing" && <ServicingTab detail={detail} patch={patch} />}
        {tab === "Funding Entities" && (
          <FundingEntitiesTab detail={detail} patch={patch} />
        )}
        {tab === "Pricing" && (
          <PricingTab detail={detail} onChanged={refresh} />
        )}
        {tab === "Overlays" && (
          <OverlaysTab detail={detail} onChanged={refresh} />
        )}
        {tab === "Agreements" && (
          <AgreementsTab detail={detail} onChanged={refresh} />
        )}
        {tab === "Notes" && <NotesTab detail={detail} onDirtyChange={setNotesDirty} />}
        {tab === "Users" && <UsersTab cid={id} detail={detail} />}
        {tab === "Admin" && (
          <CorrespondentAdminTab detail={detail} patch={patch} />
        )}
        {tab === "Loans" && (
          <LoansTab
            detail={detail}
            rows={loanRows}
            loading={loansLoading}
          />
        )}

        {/* Embedded (expanded grid row) — escape hatch to the full page, where
            the wide tabs (Pricing, Agreements) have room to breathe. It sits at
            the BOTTOM, under the tab content: it's an exit, not a header
            action, and putting it up top competed with the tab strip. Sticky so
            it stays reachable while scrolling a tall expanded row — which is
            why it's translucent + blurred rather than opaque: the content has
            to read as passing UNDER it. */}
        {onDark && !showBackLink && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              mt: 3,
              borderRadius: "10px",
              // A tint of the primary rather than a neutral slab — it reads as
              // an action strip, not another empty card.
              background: alpha(teal[50], 0.72),
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              border: `1px solid ${alpha(teal[200], 0.75)}`,
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Button
              variant="text"
              onClick={() => navigate(`/correspondents/${id}`)}
              endIcon={<OpenInNewRounded sx={{ fontSize: 15 }} />}
              sx={{
                color: teal[700],
                fontSize: 12.5,
                fontWeight: 600,
                "&:hover": { background: teal[100] },
              }}
            >
              View in full page
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function CorrespondentDrilldown() {
  const { id } = useParams();
  return <CorrespondentDetailPanel id={id} showBackLink />;
}

/* --------------------------------- Profile --------------------------------- */

// The Company/identity block is now an EDITABLE form in its own component —
// it was a wall of read-only FieldRows, so nothing on it (relationship,
// address, phone, website, volumes, bank details) could be corrected from the
// UI. Everything there saves through the same POST /{id}/update partial.
function ProfileTab({ detail, patch, onDeleted, onChanged, onOpenAgreements }) {
  return (
    <Flx column g={2.5}>
      <CorrespondentProfileTab
        detail={detail}
        patch={patch}
        onChanged={onChanged}
        onOpenAgreements={onOpenAgreements}
      />
      <DangerZone detail={detail} onDeleted={onDeleted} />
    </Flx>
  );
}

// DELETE /correspondents/{id} — hard delete, so it's gated behind typing the
// company name rather than a one-click confirm.
function DangerZone({ detail, onDeleted }) {
  const correspondentsApi = useCorrespondentsApi();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const armed = confirm.trim() === (detail.companyName || "").trim();

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await correspondentsApi.remove(docId(detail));
      onDeleted?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Delete failed");
      setBusy(false);
    }
  };

  return (
    <SectionCard title="Danger zone" icon={WarningAmberRounded}>
      <Box sx={{ fontSize: "12.5px", color: T.ink500, lineHeight: 1.6 }}>
        Deleting removes the correspondent record, its contacts, overlays and
        pricing arrangements. Loans already attributed to{" "}
        <b>{detail.companyName}</b> are not deleted, but they lose their partner
        configuration. Type the company name to enable.
      </Box>
      <Box
        sx={{ mt: 1.5, display: "flex", gap: 1, alignItems: "flex-end" }}
      >
        <Box sx={{ flex: 1 }}>
          <Label>Confirm company name</Label>
          <TextField
            size="small"
            fullWidth
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={detail.companyName}
            sx={inputSx}
          />
        </Box>
        <Button
          variant="contained"
          disabled={!armed || busy}
          startIcon={<DeleteOutlineRounded sx={{ fontSize: 16 }} />}
          onClick={run}
          sx={{
            flexShrink: 0,
            background: "#dc2626",
            color: "#fff",
            "&:hover": { background: "#b91c1c" },
          }}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </Box>
      {error && (
        <Box sx={{ mt: 1, fontSize: 12.5, color: "#b91c1c" }}>{error}</Box>
      )}
    </SectionCard>
  );
}

/* --------------------------------- Contacts -------------------------------- */

function ContactCard({ c, badge, onEdit, onDelete }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        borderRadius: "10px",
        border: `1px solid ${T.line}`,
        px: 2,
        py: 1.5,
        "&:hover .contact-actions": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: T.teal50,
          color: T.teal600,
          display: "grid",
          placeItems: "center",
          fontSize: "12px",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {contactInitials(c)}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ fontWeight: 600, color: T.ink900 }}>{contactName(c)}</Box>
          {badge && <SoftBadge tone="slate">{badge}</SoftBadge>}
          {c.role && <SoftBadge tone="slate">{c.role}</SoftBadge>}
        </Box>
        {c.title && (
          <Box sx={{ fontSize: "12.5px", color: T.ink300 }}>{c.title}</Box>
        )}
        <Box
          sx={{
            mt: 0.5,
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 16px",
            fontSize: "12.5px",
            color: T.ink500,
          }}
        >
          {c.emailAddress && (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <MailOutline sx={{ fontSize: 12, color: T.ink300 }} />{" "}
              {c.emailAddress}
            </Box>
          )}
          {c.phone && (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <PhoneOutlined sx={{ fontSize: 12, color: T.ink300 }} />{" "}
              {formatPhone(c.phone)}
            </Box>
          )}
        </Box>
      </Box>
      {(onEdit || onDelete) && (
        <Box
          className="contact-actions"
          sx={{
            display: "flex",
            gap: 0.25,
            flexShrink: 0,
            opacity: 0,
            transition: "opacity 120ms",
          }}
        >
          {onEdit && (
            <Tooltip title="Edit contact">
              <IconButton size="small" onClick={onEdit}>
                <EditOutlined sx={{ fontSize: 16, color: T.ink500 }} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Remove contact">
              <IconButton size="small" onClick={onDelete}>
                <DeleteOutlineRounded sx={{ fontSize: 16, color: "#dc2626" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}

// The three ARRAY slots, each edited in place through the typed contact CRUD
// routes (POST/DELETE /correspondents/{id}/contacts/{contactType}[/{oid}]).
// primaryContact and mlpaSignedBy are single-object slots and go through
// POST /{id}/update instead — see SingleContactSlot below.
const CONTACT_SLOTS = [
  {
    contactType: CONTACT_TYPES.additionalContacts,
    field: "additionalContacts",
    title: "Additional contacts",
    icon: MailOutline,
  },
  {
    contactType: CONTACT_TYPES.authorizedSigners,
    field: "correspondentAuthorizedSigners",
    title: "Authorized signers",
    icon: DescriptionOutlined,
  },
  {
    contactType: CONTACT_TYPES.servicingContacts,
    // Lives under the servicing object rather than at the top level.
    field: "servicing.servicingContacts",
    title: "Servicing contacts",
    icon: BusinessOutlined,
  },
];

const readSlot = (detail, path) =>
  (path.split(".").reduce((o, k) => o?.[k], detail) ?? []).filter(Boolean);

// One array slot: list + add + per-contact edit/delete.
function ContactSlotCard({ slot, detail, cid, onChanged }) {
  const correspondentsApi = useCorrespondentsApi();
  const rows = readSlot(detail, slot.field);
  const [editing, setEditing] = useState(null); // null | {} (add) | contact
  // Rejections surface inside the panel, which stays open to retry.
  const save = async (payload) => {
    const existingId = oidOf(editing);
    if (existingId) {
      // Partial $set — the panel sends only the fields that changed.
      await correspondentsApi.updateContact(
        cid,
        slot.contactType,
        existingId,
        payload,
      );
    } else {
      await correspondentsApi.addContact(cid, slot.contactType, payload);
    }
    setEditing(null);
    onChanged();
  };

  const remove = async (c) => {
    const oid = oidOf(c);
    if (!oid) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Remove ${contactName(c)} from ${slot.title}?`)) return;
    await correspondentsApi.deleteContact(cid, slot.contactType, oid);
    onChanged();
  };

  return (
    <>
      <SectionCard
        title={`${slot.title} (${rows.length})`}
        icon={slot.icon}
        action={
          <Button
            variant="text"
            size="small"
            startIcon={<AddRounded sx={{ fontSize: 16 }} />}
            onClick={() => setEditing({})}
            sx={{ color: T.teal600, fontWeight: 600, fontSize: "12px" }}
          >
            Add
          </Button>
        }
      >
        {rows.length ? (
          <Stack spacing={1}>
            {rows.map((c, i) => (
              <ContactCard
                key={oidOf(c) || i}
                c={c}
                onEdit={() => setEditing(c)}
                onDelete={() => remove(c)}
              />
            ))}
          </Stack>
        ) : (
          <Box sx={{ fontSize: 13, color: T.ink300 }}>
            No {slot.title.toLowerCase()} yet.
          </Box>
        )}
      </SectionCard>

      {editing && (
        <CorrespondentContactPanel
          title={oidOf(editing) ? "Edit Contact" : `Add to ${slot.title}`}
          subtitle={detail.companyName}
          initial={oidOf(editing) ? editing : null}
          mode="slot"
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

// primaryContact / mlpaSignedBy — single-object slots written through
// POST /{companyId}/update, not the typed CRUD routes.
function SingleContactSlot({ title, field, detail, patch, badge, extra }) {
  const [editing, setEditing] = useState(false);
  const value = detail[field];

  const save = async (payload) => {
    await patch({ [field]: payload, ...(extra ? extra(payload) : {}) });
    setEditing(false);
  };

  return (
    <>
      <SectionCard
        title={title}
        icon={MailOutline}
        action={
          <Button
            variant="text"
            size="small"
            startIcon={<EditOutlined sx={{ fontSize: 15 }} />}
            onClick={() => setEditing(true)}
            sx={{ color: T.teal600, fontWeight: 600, fontSize: "12px" }}
          >
            {value ? "Edit" : "Set"}
          </Button>
        }
      >
        {value ? (
          <ContactCard c={value} badge={badge} />
        ) : (
          <Box sx={{ fontSize: 13, color: T.ink300 }}>
            No {title.toLowerCase()} on file.
          </Box>
        )}
      </SectionCard>
      {editing && (
        <CorrespondentContactPanel
          title={title}
          subtitle={detail.companyName}
          initial={value}
          mode="object"
          onSave={save}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function ContactsTab({ detail, onChanged, patch }) {
  const cid = docId(detail);

  return (
    <FlxGrid width="440px" gap={2.5}>
      <SingleContactSlot
        title="Primary contact"
        field="primaryContact"
        detail={detail}
        patch={patch}
        badge="Primary"
      />
      {/* The MLPA signatory is no longer listed here — it's set on the
          Profile tab's Legal Agreements section (and will be read from the
          signed agreement once extraction is available). */}
      {CONTACT_SLOTS.map((slot) => (
        <ContactSlotCard
          key={slot.contactType}
          slot={slot}
          detail={detail}
          cid={cid}
          onChanged={onChanged}
        />
      ))}
    </FlxGrid>
  );
}

/* --------------------------------- Coverage -------------------------------- */

function CoverageTab({ detail, patch }) {
  const users = useSelector((s) => s.users);
  const emailOptions = useMemo(
    () =>
      (Array.isArray(users) ? users : [])
        .map((u) => u?.emailAddress)
        .filter(Boolean)
        .sort(),
    [users],
  );

  const [draft, setDraft] = useState({
    accountManager: detail.accountManager || "",
    structurer: detail.structurer || "",
    rtlReviewType: detail.rtlReviewType || "",
    dscrReviewType: detail.dscrReviewType || "",
  });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const dirty = Object.keys(draft).some(
    (k) => (draft[k] || "") !== (detail[k] || ""),
  );

  const AssignSelect = ({ k, label, hint }) => (
    <Box>
      <Label>{label}</Label>
      <TextField
        select
        size="small"
        fullWidth
        value={draft[k]}
        onChange={(e) => set(k, e.target.value)}
        sx={inputSx}
      >
        <MenuItem value="">— Unassigned —</MenuItem>
        {emailOptions.map((o) => (
          <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
            {o}
          </MenuItem>
        ))}
      </TextField>
      {hint && (
        <Box sx={{ mt: 0.5, fontSize: "11.5px", color: T.ink300 }}>{hint}</Box>
      )}
    </Box>
  );

  const WorkflowSelect = ({ k, label, options }) => (
    <Box>
      <Label>{label}</Label>
      <TextField
        select
        size="small"
        fullWidth
        value={draft[k]}
        onChange={(e) => set(k, e.target.value)}
        sx={inputSx}
      >
        <MenuItem value="">— Unassigned —</MenuItem>
        {options.map((o) => (
          <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
            {o}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <Box>
      <FlxGrid width="440px" gap={2.5}>
        <SectionCard title="Eastview coverage" icon={BusinessOutlined}>
          <FlxGrid width="320px" gap={4}>
            <AssignSelect
              k="accountManager"
              label="Account Manager"
              hint="Auto-copied onto every new loan for this partner."
            />
            <AssignSelect
              k="structurer"
              label="Structurer"
              hint="Auto-copied onto every new loan."
            />
          </FlxGrid>
        </SectionCard>
        <SectionCard title="Review workflows" icon={DescriptionOutlined}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <WorkflowSelect
              k="rtlReviewType"
              label="RTL workflow"
              options={[
                "Pre-Close Full",
                "Post-Close Light",
                "Not Selling to EV",
              ]}
            />
            <WorkflowSelect
              k="dscrReviewType"
              label="DSCR workflow"
              options={[
                "Pre-Close Full",
                "Not Selling to EV",
                "Not Originating",
              ]}
            />
          </Box>
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              fontSize: "12px",
              color: T.ink300,
            }}
          >
            <InfoOutlined sx={{ fontSize: 14, mt: "1px" }} />
            Workflow types control how much review each product gets
            before/after close for this partner.
          </Box>
        </SectionCard>
      </FlxGrid>
      <PendingChangesBar
        dirty={dirty}
        onSave={() => patch(draft)}
        label="Save coverage"
      />
    </Box>
  );
}

/* -------------------------------- Servicing -------------------------------- */
// The correspondent servicing block (correspondent-admin-and-locks.md §5b): the
// FCI/Shellpoint accounts a correspondent boards loans under, the default
// servicer, and the `authorizedToDirectServicer` oversight control. Saves the
// WHOLE `servicing` object through the same partial update — spread the existing
// object so servicingContacts (managed by the Contacts tab) survive.
function ServicingTab({ detail, patch }) {
  const servicing = useMemo(() => detail.servicing || {}, [detail.servicing]);
  const savedAccounts = useMemo(
    () => (servicing.servicerAccounts || []).filter(Boolean),
    [servicing.servicerAccounts],
  );

  const [draft, setDraft] = useState({
    defaultServicer: servicing.defaultServicer || "",
    authorizedToDirectServicer: !!servicing.authorizedToDirectServicer,
    servicerDirectiveAuthorityNotes: servicing.servicerDirectiveAuthorityNotes || "",
    specialServicingNotes: servicing.specialServicingNotes || "",
    boardingNotes: servicing.boardingNotes || "",
  });
  const [accounts, setAccounts] = useState(() =>
    savedAccounts.map((a) => ({ ...a })),
  );

  // §5c — third-party services BEYOND the servicer (TrustPoint draws, etc).
  // EV was prepopulated 2026-08-12 from the servicing tracker, so existing rows
  // (including `Attention` flags) render immediately; the editor is for
  // corrections + new providers.
  const savedServices = useMemo(
    () => (servicing.additionalServices || []).filter(Boolean),
    [servicing.additionalServices],
  );
  const [services, setServices] = useState(() =>
    savedServices.map((s) => ({ ...s })),
  );

  // `fundingEntities[]` is edited on its own **Funding Entities** tab, not here
  // — it's a top-level field rather than part of `servicing`, and two editors
  // over the same array would fight each other.

  // Resync the row editors whenever the server value changes. This is not
  // cosmetic: the backend assigns `oid` to new rows, so after a save the
  // refreshed rows no longer deep-equal the local drafts and the dirty check
  // below would stay true forever — the Save button never returns to "No
  // pending changes" and a second click re-POSTs rows that already exist.
  useEffect(() => {
    setAccounts(savedAccounts.map((a) => ({ ...a })));
  }, [savedAccounts]);
  useEffect(() => {
    setServices(savedServices.map((s) => ({ ...s })));
  }, [savedServices]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const scalarDirty =
    (draft.defaultServicer || "") !== (servicing.defaultServicer || "") ||
    !!draft.authorizedToDirectServicer !==
      !!servicing.authorizedToDirectServicer ||
    (draft.servicerDirectiveAuthorityNotes || "") !==
      (servicing.servicerDirectiveAuthorityNotes || "") ||
    (draft.specialServicingNotes || "") !==
      (servicing.specialServicingNotes || "") ||
    (draft.boardingNotes || "") !== (servicing.boardingNotes || "");
  const accountsDirty =
    JSON.stringify(accounts) !== JSON.stringify(savedAccounts);
  const servicesDirty =
    JSON.stringify(services) !== JSON.stringify(savedServices);
  const servicingDirty = scalarDirty || accountsDirty || servicesDirty;
  const dirty = servicingDirty;

  const addAccount = () =>
    setAccounts((a) => [
      ...a,
      {
        servicer: "",
        servicerAccountId: "",
        accountName: "",
        isDefault: false,
        active: true,
        notes: "",
      },
    ]);
  const setAccount = (i, k, v) =>
    setAccounts((a) => a.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeAccount = (i) =>
    setAccounts((a) => a.filter((_, idx) => idx !== i));

  const addService = () =>
    setServices((s) => [
      ...s,
      { provider: "", providerAccountId: "", status: "", notes: "" },
    ]);
  const setService = (i, k, v) =>
    setServices((s) => s.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeService = (i) =>
    setServices((s) => s.filter((_, idx) => idx !== i));

  // One partial update, only the keys that actually changed. `servicing` is a
  // nested object so the whole thing is sent merged (never just one array);
  // `fundingEntities` is top-level and sent alongside it.
  const save = () => {
    const body = {};
    if (servicingDirty) {
      body.servicing = {
        ...servicing,
        ...draft,
        servicerAccounts: accounts,
        additionalServices: services,
      };
    }
    if (Object.keys(body).length) patch(body);
  };

  return (
    <Box>
      <FlxGrid width="480px" gap={2.5}>
        <SectionCard
          title="Servicer accounts"
          icon={BusinessOutlined}
          action={
            <Button
              size="small"
              variant="text"
              startIcon={<AddRounded sx={{ fontSize: 17 }} />}
              onClick={addAccount}
            >
              Add account
            </Button>
          }
        >
          {accounts.length === 0 ? (
            <Box sx={{ fontSize: "13px", color: T.ink300, py: 1 }}>
              No servicer accounts yet — add the FCI / Shellpoint lender id(s)
              this correspondent boards loans under.
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {accounts.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    border: `1px solid ${T.line}`,
                    borderRadius: "10px",
                    p: 1.5,
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Label>Servicer</Label>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.servicer || ""}
                        onChange={(e) => setAccount(i, "servicer", e.target.value)}
                        sx={inputSx}
                      >
                        <MenuItem value="">— Select —</MenuItem>
                        {SERVICER_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                            {o}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box>
                      <Label>Servicer account / lender id</Label>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.servicerAccountId || ""}
                        onChange={(e) =>
                          setAccount(i, "servicerAccountId", e.target.value)
                        }
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Label>Account name</Label>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.accountName || ""}
                        onChange={(e) =>
                          setAccount(i, "accountName", e.target.value)
                        }
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Label>Notes</Label>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.notes || ""}
                        onChange={(e) => setAccount(i, "notes", e.target.value)}
                        sx={inputSx}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mt: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!row.isDefault}
                          onChange={(e) =>
                            setAccount(i, "isDefault", e.target.checked)
                          }
                        />
                      }
                      label="Default"
                      sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={row.active !== false}
                          onChange={(e) =>
                            setAccount(i, "active", e.target.checked)
                          }
                        />
                      }
                      label="Active"
                      sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => removeAccount(i)}>
                      <DeleteOutlineRounded sx={{ fontSize: 18, color: "#b91c1c" }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>

        <SectionCard title="Servicing setup" icon={BusinessOutlined}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Label>Default servicer</Label>
              <TextField
                select
                size="small"
                fullWidth
                value={draft.defaultServicer}
                onChange={(e) => set("defaultServicer", e.target.value)}
                sx={inputSx}
              >
                <MenuItem value="">— Not set —</MenuItem>
                {SERVICER_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ mt: 0.5, fontSize: "11.5px", color: T.ink300 }}>
                Where new loans from this correspondent board by default.
              </Box>
            </Box>

            <Box
              sx={{
                border: `1px solid ${
                  draft.authorizedToDirectServicer ? "#f59e0b" : T.line
                }`,
                borderRadius: "10px",
                p: 1.5,
                background: draft.authorizedToDirectServicer
                  ? "#fffbeb"
                  : "transparent",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!draft.authorizedToDirectServicer}
                    onChange={(e) =>
                      set("authorizedToDirectServicer", e.target.checked)
                    }
                  />
                }
                label="Authorized to direct the servicer"
                sx={{ "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 600 } }}
              />
              <Box sx={{ fontSize: "11.5px", color: T.ink300, mt: 0.25 }}>
                Oversight control — default OFF. May this correspondent direct the
                servicer (rate/term changes) directly? Most should stay OFF; a
                change at the servicer attributed to a correspondent NOT authorized
                here gets flagged by servicing reconciliation.
              </Box>
            </Box>

            <Box>
              <Label>Directive authority notes</Label>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={draft.servicerDirectiveAuthorityNotes}
                onChange={(e) =>
                  set("servicerDirectiveAuthorityNotes", e.target.value)
                }
                sx={inputSx}
              />
            </Box>
            <Box>
              <Label>Special servicing notes</Label>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={draft.specialServicingNotes}
                onChange={(e) => set("specialServicingNotes", e.target.value)}
                sx={inputSx}
              />
            </Box>
            <Box>
              <Label>Boarding notes</Label>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={draft.boardingNotes}
                onChange={(e) => set("boardingNotes", e.target.value)}
                sx={inputSx}
              />
            </Box>
          </Box>
        </SectionCard>

        {/* §5c — additional services (onboarding to third-party providers). */}
        <SectionCard
          title={`Additional services (${services.length})`}
          icon={DescriptionOutlined}
          action={
            <Button
              size="small"
              variant="text"
              startIcon={<AddRounded sx={{ fontSize: 17 }} />}
              onClick={addService}
            >
              Add service
            </Button>
          }
        >
          <Box sx={{ fontSize: "11.5px", color: T.ink300, mb: 1.5 }}>
            Third-party services this correspondent is onboarded to{" "}
            <b>beyond the servicer</b> — e.g. TrustPoint draw management.{" "}
            <b>Attention</b> means flagged for follow-up.
          </Box>
          {services.length === 0 ? (
            <Box sx={{ fontSize: "13px", color: T.ink300, py: 1 }}>
              No additional services recorded.
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {services.map((row, i) => (
                <Box
                  key={row.oid || i}
                  sx={{
                    border: `1px solid ${
                      row.status === "Attention" ? "#fde68a" : T.line
                    }`,
                    background:
                      row.status === "Attention"
                        ? statusColors.warning.bg
                        : "transparent",
                    borderRadius: "10px",
                    p: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Label>Provider</Label>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.provider || ""}
                        onChange={(e) =>
                          setService(i, "provider", e.target.value)
                        }
                        sx={inputSx}
                      >
                        <MenuItem value="">— Select —</MenuItem>
                        {SERVICE_PROVIDER_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                            {o}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box>
                      <Label>Status</Label>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.status || ""}
                        onChange={(e) => setService(i, "status", e.target.value)}
                        sx={inputSx}
                      >
                        <MenuItem value="">— Not set —</MenuItem>
                        {SERVICE_PROVIDER_STATUS_OPTIONS.map((o) => (
                          <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                            {o}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Label>Provider account id</Label>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.providerAccountId || ""}
                        onChange={(e) =>
                          setService(i, "providerAccountId", e.target.value)
                        }
                        placeholder="Optional until known"
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Label>Notes</Label>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.notes || ""}
                        onChange={(e) => setService(i, "notes", e.target.value)}
                        sx={inputSx}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    {row.status && (
                      <SoftBadge tone={SERVICE_STATUS_TONE[row.status]}>
                        {row.status}
                      </SoftBadge>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => removeService(i)}>
                      <DeleteOutlineRounded
                        sx={{ fontSize: 18, color: statusColors.error.fg }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>

      </FlxGrid>

      <PendingChangesBar dirty={dirty} onSave={save} label="Save servicing" />
    </Box>
  );
}

/* ----------------------------- Funding Entities ---------------------------- */
// The closing/funding entities a correspondent originates and closes loans in —
// the entity that appears as lender/mortgagee on the note. Add/remove list of
// entity cards, saved as the WHOLE `fundingEntities` array through the same
// partial update (`patch`) the other tabs use. `mortgageeClause` is a backend-
// computed, READ-ONLY string (NAME ISAOA/ATIMA + address) — displayed, never
// edited or sent back.
function FundingEntitiesTab({ detail, patch }) {
  const savedEntities = useMemo(
    () => (detail.fundingEntities || []).filter(Boolean),
    [detail.fundingEntities],
  );

  const [entities, setEntities] = useState(() =>
    savedEntities.map((e) => ({ ...e })),
  );
  const [saving, setSaving] = useState(false);

  // Keep local edits, but re-sync when the underlying record changes (e.g. after
  // a save round-trips the backend-computed mortgageeClause back down).
  useEffect(() => {
    setEntities(savedEntities.map((e) => ({ ...e })));
  }, [savedEntities]);

  const dirty = JSON.stringify(entities) !== JSON.stringify(savedEntities);

  // How many rows actually differ — added/removed rows plus edited ones — so
  // the save pill can say "2 unsaved changes" rather than a vague "you have
  // unsaved changes".
  const changedCount = useMemo(() => {
    let n = Math.abs(entities.length - savedEntities.length);
    const shared = Math.min(entities.length, savedEntities.length);
    for (let i = 0; i < shared; i += 1) {
      if (JSON.stringify(entities[i]) !== JSON.stringify(savedEntities[i])) {
        n += 1;
      }
    }
    return n;
  }, [entities, savedEntities]);

  const addEntity = () =>
    setEntities((list) => [
      ...list,
      {
        name: "",
        shortName: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
        isaoaAtima: true,
        active: true,
        notes: "",
      },
    ]);
  const setEntity = (i, k, v) =>
    setEntities((list) =>
      list.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)),
    );
  const removeEntity = (i) =>
    setEntities((list) => list.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      // Drop the read-only computed clause so we never write it back.
      await patch({
        fundingEntities: entities.map(({ mortgageeClause, ...rest }) => rest),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* The card UI lives in its own component — collapsible rows and a
          width-capped 12-column form, so the inputs keep a readable measure
          instead of stretching the full width of the panel. */}
      <FundingEntitiesEditor
        entities={entities}
        onAdd={addEntity}
        onChange={setEntity}
        onRemove={removeEntity}
      />

      <PendingChangesBar
        dirty={dirty}
        count={changedCount}
        saving={saving}
        onSave={save}
        onDiscard={() => setEntities(savedEntities.map((e) => ({ ...e })))}
        label="Save funding entities"
      />
    </Box>
  );
}

/* --------------------------------- Pricing --------------------------------- */

// "On this page" rail — mirrors the section ids below, laid out the same way
// as the Profile tab (PricingPageLayout + ProfileSection cards).
const PRICING_RAIL = [
  { id: "pricing-dscr", label: "DSCR pricing" },
  { id: "pricing-terms", label: "Counterparty terms" },
  { id: "pricing-rtl", label: "RTL / GUC pricing" },
  { id: "pricing-lock", label: "Lock configuration" },
  { id: "pricing-adjustments", label: "Price adjustments" },
  { id: "pricing-audit", label: "Audit trail" },
];

function PricingTab({ detail, onChanged }) {
  const correspondentsApi = useCorrespondentsApi();
  const cid = docId(detail);

  const tier = detail.pricingTier ?? detail.dscr_pricing_tier ?? "Silver";
  const additional = detail.dscr_additional_margin ?? 0;
  const override = detail.dscr_margin_override_points ?? null;

  // DB-sourced tier margins + RTL product taxonomy (fall back to the in-code
  // mirrors before the collections are seeded / if the fetch fails).
  const [tierMargins, setTierMargins] = useState(TIER_MARGINS);
  const [rtlProducts, setRtlProducts] = useState(RTL_SPREAD_PRODUCTS);
  // RTL/GUC tiers come from the rtl_tier_spreads collection only — no in-code
  // fallback list. null = loading, "error" = the fetch failed.
  const [rtlTiers, setRtlTiers] = useState(null);
  // What the backend actually prices per RTL product (GET /{id}/pricing →
  // rtlEffectiveSpreads), resolved exactly as the sizer and the lock resolve it.
  // Refetched whenever the record reloads (after every save).
  const [rtlEffective, setRtlEffective] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // The two fields a save can be blocked on — a blocked save scrolls to and
  // focuses the one that's missing (the save bar has no disabled state, and
  // the Reason box sits at the bottom of the tab, usually off-screen).
  const reasonInputRef = useRef(null);
  const noteInputRef = useRef(null);
  const focusField = (ref) => {
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  useEffect(() => {
    let alive = true;
    if (!cid) return undefined;
    correspondentsApi
      .effectivePricing(cid)
      .then((p) => alive && setRtlEffective(p?.rtlEffectiveSpreads || null))
      .catch(() => alive && setRtlEffective(null));
    return () => {
      alive = false;
    };
  }, [correspondentsApi, cid, detail]);

  useEffect(() => {
    let alive = true;
    correspondentsApi
      .listRtlTierSpreads()
      .then((rows) => alive && setRtlTiers(Array.isArray(rows) ? rows : []))
      .catch(() => alive && setRtlTiers("error"));
    correspondentsApi
      .listTierMargins()
      .then((list) => {
        if (!alive || !Array.isArray(list) || !list.length) return;
        const m = {};
        list.forEach((d) => {
          const t = d._id ?? d.tier;
          if (t && d.marginPoints != null) m[t] = d.marginPoints;
        });
        if (Object.keys(m).length) setTierMargins(m);
      })
      .catch(() => {});
    correspondentsApi
      .listRtlBaseRates()
      .then((res) => {
        if (!alive) return;
        const prods = Array.isArray(res?.products) ? res.products : null;
        if (prods?.length) setRtlProducts(prods);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [correspondentsApi]);

  // The saved values, as the form holds them. One builder so the initial state
  // and Cancel can't drift apart.
  const buildDraft = useCallback(
    () => ({
      tier,
      additionalMargin: additional,
      overridePoints: override,
      // Free text on the model — "Purchase 8.125% / R-T 8.25%" is as valid as a
      // single rate. They live here rather than on Profile because they route
      // through the AUDITED pricing endpoint.
      buyRate: detail.buyRate ?? "",
      tfFee: detail.tfFee ?? "",
      rtlSpreads: spreadDecToPct(detail.rtl_pricing_spreads),
      rtlTier: detail.rtl_pricing_tier ?? "",
      rtlOverlay:
        detail.rtl_additional_spread_pct == null
          ? ""
          : String(detail.rtl_additional_spread_pct),
      rtlOverlayNote: detail.rtl_spread_overlay_note ?? "",
      maxLockDays: detail.max_lock_days ?? "",
      feeBpsPerDay:
        detail.lock_extension_fee_bps_per_day == null
          ? 2.0
          : detail.lock_extension_fee_bps_per_day,
      feeOptions: Array.isArray(detail.lock_extension_fee_options)
        ? detail.lock_extension_fee_options.map((o) => ({
            days: o?.days ?? "",
            bps: o?.bps ?? "",
          }))
        : [],
    }),
    [tier, additional, override, detail],
  );

  const [draft, setDraft] = useState(buildDraft);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const cancelChanges = () => {
    setDraft(buildDraft());
    setReason("");
  };

  const baselineSpreads = spreadDecToPct(detail.rtl_pricing_spreads);
  const baselineFeeOptions = (
    Array.isArray(detail.lock_extension_fee_options)
      ? detail.lock_extension_fee_options
      : []
  ).map((o) => ({ days: o?.days ?? "", bps: o?.bps ?? "" }));

  // RTL/GUC tier + overlay (all PERCENT). Blank tier / note save as null and a
  // blank overlay as 0, so an untouched record never writes an audit entry.
  const rtlTierValue = draft.rtlTier || null;
  const rtlOverlayValue = Number(draft.rtlOverlay || 0);
  const rtlNoteValue = String(draft.rtlOverlayNote || "").trim() || null;
  const rtlTierChanged = rtlTierValue !== (detail.rtl_pricing_tier || null);
  const rtlOverlayChanged =
    rtlOverlayValue !== Number(detail.rtl_additional_spread_pct || 0);
  const rtlNoteChanged =
    rtlNoteValue !== (String(detail.rtl_spread_overlay_note || "").trim() || null);
  // An unexplained negotiated spread is what the audit trail exists to catch.
  const rtlNoteMissing = rtlOverlayValue !== 0 && !rtlNoteValue;

  const dirty =
    draft.tier !== tier ||
    (draft.buyRate ?? "") !== (detail.buyRate ?? "") ||
    (draft.tfFee ?? "") !== (detail.tfFee ?? "") ||
    Number(draft.additionalMargin) !== Number(additional) ||
    (draft.overridePoints ?? null) !== (override ?? null) ||
    JSON.stringify(draft.rtlSpreads || {}) !== JSON.stringify(baselineSpreads) ||
    rtlTierChanged ||
    rtlOverlayChanged ||
    rtlNoteChanged ||
    String(draft.maxLockDays ?? "") !== String(detail.max_lock_days ?? "") ||
    Number(draft.feeBpsPerDay ?? "") !==
      Number(detail.lock_extension_fee_bps_per_day ?? 2.0) ||
    JSON.stringify(draft.feeOptions || []) !==
      JSON.stringify(baselineFeeOptions);

  const hasOverride =
    draft.overridePoints != null && draft.overridePoints !== "";
  const tierMargin = tierMargins[draft.tier] ?? 0;
  const resolved = hasOverride
    ? Number(draft.overridePoints)
    : tierMargin + Number(draft.additionalMargin || 0);
  // Effective-margin indicator: highlighted whenever pricing departs from the
  // plain tier margin (an override is set, or extra margin is added).
  const marginAdjusted =
    hasOverride || Number(draft.additionalMargin || 0) !== 0;

  // Order RTL products parents-first with secondaries following their parent.
  const orderedRtlProducts = useMemo(() => {
    const parents = rtlProducts.filter((p) => !p.secondary_of);
    const out = [];
    parents.forEach((p) => {
      out.push(p);
      rtlProducts
        .filter((c) => c.secondary_of === p.key)
        .forEach((c) => out.push(c));
    });
    rtlProducts
      .filter(
        (p) => p.secondary_of && !parents.some((x) => x.key === p.secondary_of),
      )
      .forEach((p) => out.push(p));
    return out;
  }, [rtlProducts]);

  // Tier options: active rows of rtl_tier_spreads, plus the saved tier even if
  // it has since been retired or removed, so the select never blanks it.
  const rtlTierRows = Array.isArray(rtlTiers) ? rtlTiers : [];
  const rtlTierRow = (t) => rtlTierRows.find((r) => (r._id ?? r.tier) === t);
  const rtlTierOptions = useMemo(() => {
    const rows = Array.isArray(rtlTiers) ? rtlTiers : [];
    const opts = rows
      .filter((r) => r.active !== false)
      // Each option carries its tier spread (Gold +0.125%, Silver +0.25%,
      // Bronze +0.375% by default), so the pick shows what it adds to the rate.
      .map((r) => ({
        value: r._id ?? r.tier,
        label: `${r.label || r._id || r.tier} · ${
          rtlTierUnpriced(r)
            ? "not yet priced"
            : `+${trimZeros(Number(r.spreadPct || 0).toFixed(3)) || "0"}%`
        }`,
      }))
      .filter((o) => o.value);
    const saved = detail.rtl_pricing_tier;
    if (saved && !opts.some((o) => o.value === saved)) {
      const row = rows.find((r) => (r._id ?? r.tier) === saved);
      opts.push({
        value: saved,
        label: `${saved} (${row ? "inactive" : "not in tier table"})`,
      });
    }
    return opts;
  }, [rtlTiers, detail.rtl_pricing_tier]);

  // The tier RTL/GUC actually prices on (owner 2026-10-08): the RTL-specific
  // tier when one is picked, else the CORRESPONDENT's tier (pricingTier, the
  // Gold/Silver/Bronze badge — the DSCR tier above). `tier` defaults to Silver
  // for display, so the stored value is used unless the DSCR tier was changed
  // in this draft.
  const correspondentTier =
    draft.tier !== tier
      ? draft.tier
      : detail.pricingTier || detail.dscr_pricing_tier || null;
  const effectiveRtlTier = draft.rtlTier || correspondentTier || null;
  const rtlTierFromCorrespondent = !draft.rtlTier && !!correspondentTier;

  const selectedTierRow = effectiveRtlTier ? rtlTierRow(effectiveRtlTier) : null;
  const selectedTierUnpriced =
    !!effectiveRtlTier && (!selectedTierRow || rtlTierUnpriced(selectedTierRow));
  const selectedTierSpread = selectedTierRow
    ? Number(selectedTierRow.spreadPct || 0)
    : null;
  const fallbackTierRow = correspondentTier ? rtlTierRow(correspondentTier) : null;
  const fallbackTierLabel = correspondentTier
    ? `${correspondentTier}${
        fallbackTierRow && !rtlTierUnpriced(fallbackTierRow)
          ? ` · +${trimZeros(Number(fallbackTierRow.spreadPct || 0).toFixed(3)) || "0"}%`
          : ""
      }`
    : null;

  // RTL inputs untouched since the last save → show the backend's own answer
  // (rtlEffectiveSpreads). While editing, preview the same resolution locally:
  // a per-product override wins; else a legacy single spread; else the tier
  // (RTL tier, or the correspondent's). The overlay adds on top in every case.
  const rtlInputsDirty =
    rtlTierChanged ||
    rtlOverlayChanged ||
    draft.tier !== tier ||
    JSON.stringify(draft.rtlSpreads || {}) !== JSON.stringify(baselineSpreads);
  const resolveRtlProduct = (key) => {
    const server = !rtlInputsDirty ? rtlEffective?.[key] : null;
    if (server) {
      const basis =
        {
          product_override: "override",
          legacy_product_spread: "legacy",
          tier: "tier",
          tier_unseeded: "unpriced",
          overlay_only: "none",
          none: "none",
        }[server.basis] || "none";
      return {
        base: server.tierSpreadPct ?? null,
        basis,
        tierName: server.tier,
        fromCorrespondent: server.tierSource === "correspondent_tier",
        total: server.totalSpreadPct,
      };
    }
    const typed = draft.rtlSpreads?.[key];
    if (typed !== "" && typed != null && !Number.isNaN(Number(typed))) {
      return { base: Number(typed), basis: "override" };
    }
    // Deprecated single spreads still win over the tier when the map has no
    // entry for the product (backend fallback). Not editable here — flagged.
    const legacy =
      key === "ground_up_construction"
        ? detail.ground_up_pricing_spread
        : detail.rtl_pricing_spread;
    if (legacy != null && !Number.isNaN(Number(legacy))) {
      return { base: Number(legacy) * 100, basis: "legacy" };
    }
    if (effectiveRtlTier) {
      return {
        base: selectedTierRow ? selectedTierSpread : 0,
        basis: selectedTierUnpriced ? "unpriced" : "tier",
        tierName: effectiveRtlTier,
        fromCorrespondent: rtlTierFromCorrespondent,
      };
    }
    return { base: null, basis: "none" };
  };
  const rtlOverrideCount = orderedRtlProducts.filter(
    (p) => resolveRtlProduct(p.key).basis === "override",
  ).length;
  // No tier anywhere (no RTL tier AND no correspondent tier) but per-product
  // spreads on file = still on the pre-tier setup. It prices; prompt to pick.
  const rtlLegacyOnly =
    !effectiveRtlTier &&
    orderedRtlProducts.some((p) => resolveRtlProduct(p.key).basis !== "none");

  const setSpread =(product, value) =>
    setDraft((d) => ({
      ...d,
      rtlSpreads: { ...(d.rtlSpreads || {}), [product]: value },
    }));

  const setFeeOption = (idx, key, value) =>
    setDraft((d) => {
      const next = (d.feeOptions || []).map((o, i) =>
        i === idx ? { ...o, [key]: value } : o,
      );
      return { ...d, feeOptions: next };
    });
  const addFeeOption = () =>
    setDraft((d) => ({
      ...d,
      feeOptions: [...(d.feeOptions || []), { days: "", bps: "" }],
    }));
  const removeFeeOption = (idx) =>
    setDraft((d) => ({
      ...d,
      feeOptions: (d.feeOptions || []).filter((_, i) => i !== idx),
    }));

  const adjustments = detail.correspondent_price_adjustments ?? [];
  const audit = detail.pricingAuditTrail ?? [];

  // ── Save: one audited endpoint per card (correspondent-admin-and-locks.md,
  // "Product-specific pricing updates", owner 2026-10-08). Each body holds ONLY
  // the fields the user changed:
  //   RTL card  → POST /{id}/pricing/rtl     (reason REQUIRED, strict fields)
  //   DSCR card → POST /{id}/pricing/dscr    (reason REQUIRED, strict fields)
  //   terms + lock → POST /{id}/pricing/update (PATCH; '' ignored, null clears)
  // so the RTL card's save can never touch DSCR or lock settings, and vice versa.
  const dscrBody = {};
  if (draft.tier !== tier) {
    dscrBody.pricingTier = draft.tier;
    dscrBody.dscr_pricing_tier = draft.tier;
  }
  if (Number(draft.additionalMargin || 0) !== Number(additional || 0))
    dscrBody.dscr_additional_margin = Number(draft.additionalMargin || 0);
  if ((draft.overridePoints ?? null) !== (override ?? null))
    dscrBody.dscr_margin_override_points =
      draft.overridePoints === "" || draft.overridePoints == null
        ? null
        : Number(draft.overridePoints);

  const rtlBody = {};
  if (rtlTierChanged) rtlBody.rtl_pricing_tier = rtlTierValue;
  if (rtlOverlayChanged) rtlBody.rtl_additional_spread_pct = rtlOverlayValue;
  if (rtlNoteChanged) rtlBody.rtl_spread_overlay_note = rtlNoteValue;
  if (JSON.stringify(draft.rtlSpreads || {}) !== JSON.stringify(baselineSpreads))
    rtlBody.rtl_pricing_spreads = spreadPctToDec(draft.rtlSpreads);

  // A cleared text field is sent as null (an explicit clear) — '' would be
  // ignored by the endpoint and the old value would stay.
  const termsBody = {};
  if ((draft.buyRate ?? "") !== (detail.buyRate ?? ""))
    termsBody.buyRate = String(draft.buyRate || "").trim() || null;
  if ((draft.tfFee ?? "") !== (detail.tfFee ?? ""))
    termsBody.tfFee = String(draft.tfFee || "").trim() || null;
  if (String(draft.maxLockDays ?? "") !== String(detail.max_lock_days ?? ""))
    termsBody.max_lock_days =
      draft.maxLockDays === "" || draft.maxLockDays == null
        ? null
        : parseInt(draft.maxLockDays, 10);
  if (
    Number(draft.feeBpsPerDay ?? "") !==
    Number(detail.lock_extension_fee_bps_per_day ?? 2.0)
  )
    termsBody.lock_extension_fee_bps_per_day =
      draft.feeBpsPerDay === "" || draft.feeBpsPerDay == null
        ? null
        : Number(draft.feeBpsPerDay);
  if (JSON.stringify(draft.feeOptions || []) !== JSON.stringify(baselineFeeOptions))
    termsBody.lock_extension_fee_options = (draft.feeOptions || [])
      .filter((o) => o.days !== "" && o.bps !== "")
      .map((o) => ({ days: Number(o.days), bps: Number(o.bps) }));

  const hasRtl = Object.keys(rtlBody).length > 0;
  const hasDscr = Object.keys(dscrBody).length > 0;
  const hasTerms = Object.keys(termsBody).length > 0;
  const reasonText = String(reason || "").trim();
  // RTL and DSCR saves are refused by the backend without a reason.
  const reasonMissing = (hasRtl || hasDscr) && !reasonText;

  // The product endpoints answer a bad body with { error, fields?, allowed? }.
  const errorText = (e) => {
    const d = e?.response?.data?.detail ?? e?.response?.data;
    if (d && typeof d === "object") {
      return [d.error, d.fields?.length ? `(${d.fields.join(", ")})` : null]
        .filter(Boolean)
        .join(" ");
    }
    return d || e?.message || String(e);
  };

  const save = async () => {
    // PendingChangesBar has no disabled state — refuse here; the fields and
    // the bar's sub-text say why.
    if (rtlNoteMissing) {
      focusField(noteInputRef);
      return;
    }
    if (reasonMissing) {
      focusField(reasonInputRef);
      return;
    }
    setSaving(true);
    setSaveError(null);
    const steps = [
      hasRtl && {
        label: "RTL pricing",
        run: () =>
          correspondentsApi.updateRtlPricing(cid, { ...rtlBody, reason: reasonText }),
      },
      hasDscr && {
        label: "DSCR pricing",
        run: () =>
          correspondentsApi.updateDscrPricing(cid, { ...dscrBody, reason: reasonText }),
      },
      hasTerms && {
        label: "Terms and lock settings",
        run: () =>
          correspondentsApi.updatePricingArrangements(cid, {
            ...termsBody,
            ...(reasonText ? { reason: reasonText } : {}),
          }),
      },
    ].filter(Boolean);
    if (!steps.length) {
      // Shouldn't happen (the bar only shows with edits) — say so rather than
      // looking like the click did nothing.
      setSaving(false);
      setSaveError("No pricing changes to save.");
      return;
    }
    const done = [];
    try {
      for (const step of steps) {
        // eslint-disable-next-line no-await-in-loop
        await step.run();
        done.push(step.label);
      }
      setReason("");
    } catch (e) {
      setSaveError(
        `${steps[done.length]?.label || "Pricing"} was not saved: ${errorText(e)}${
          done.length ? ` (${done.join(" and ")} saved)` : ""
        }`,
      );
    } finally {
      setSaving(false);
      // Reload whatever did land, so the form re-bases on the saved record.
      if (done.length) onChanged();
    }
  };

  // Hint under a field — helper-text size, amber when it flags something.
  // Helper text under a field — the FIELD_TEXT.helper standard (11.5px,
  // #64748B, 4px below the control); `warn` keeps the amber for a flagged line.
  const hint = (children, warn) => (
    <Box
      sx={{
        ...FIELD_TEXT.helper,
        ...(warn ? { color: statusColors.warning.fg } : null),
      }}
    >
      {children}
    </Box>
  );

  return (
    <>
      <PricingPageLayout nav={PRICING_RAIL}>
        <LastPricingChangeStrip detail={detail} />
        {saveError ? (
          <Alert severity="error" onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        ) : null}
        <ProfileSection
          id="pricing-dscr"
          title="DSCR pricing"
          desc="Margins are bond points (par = 100). The tier sets the margin, additional margin adds on top, and an override replaces both."
        >
          <Grid>
            <Cell span={4} spanSm={6}>
              <PricingField label="Pricing tier">
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={draft.tier}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, tier: e.target.value }))
                  }
                  sx={FIELD_SX}
                >
                  {Object.keys(tierMargins).map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </PricingField>
              {hint(
                <>
                  Tier margin: <b>{fmtPts(tierMargin)}</b>
                </>,
              )}
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Additional margin (pts)">
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ step: 0.125 }}
                  disabled={hasOverride}
                  value={draft.additionalMargin}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      additionalMargin: e.target.value,
                    }))
                  }
                  sx={FIELD_SX}
                />
              </PricingField>
              {hint("Added on top of the tier margin.")}
            </Cell>
            <Cell span={4} spanSm={12}>
              <PricingField label="Margin override (pts)">
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  placeholder="—"
                  inputProps={{ step: 0.125 }}
                  value={draft.overridePoints ?? ""}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      overridePoints:
                        e.target.value === "" ? null : e.target.value,
                    }))
                  }
                  sx={FIELD_SX}
                />
              </PricingField>
              {hint("Bespoke — replaces tier + additional entirely.", true)}
            </Cell>
            <Cell span={12}>
              <EffectiveStrip
                label="Effective DSCR margin"
                detail={
                  hasOverride
                    ? "override in effect"
                    : `${draft.tier} ${fmtPts(tierMargin)} + ${fmtPts(Number(draft.additionalMargin || 0))} additional`
                }
                value={fmtPts(resolved)}
                adjusted={marginAdjusted}
              />
            </Cell>
          </Grid>
        </ProfileSection>

        {/* Buy rate + table-funding fee: free-text on the model, used to
            prepopulate the admin grid and lock/closing. Edited here rather than
            on Profile because this tab's save is the AUDITED pricing endpoint —
            routing them through the plain update would drop the audit entry. */}
        <ProfileSection
          id="pricing-terms"
          title="Counterparty terms"
          desc="Free text on the record — used to prepopulate the admin grid and lock / closing."
        >
          <Grid>
            <Cell span={6}>
              <PricingField
                label="Buy rate"
                helperText="One rate or per-purpose text."
              >
                <TextField
                  size="small"
                  fullWidth
                  value={draft.buyRate ?? ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, buyRate: e.target.value }))
                  }
                  placeholder='e.g. "Purchase 8.125% / R-T 8.25%"'
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={6}>
              <PricingField label="Table-funding fee">
                <TextField
                  size="small"
                  fullWidth
                  value={draft.tfFee ?? ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, tfFee: e.target.value }))
                  }
                  placeholder='e.g. "25 bps" or "0.25%"'
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
          </Grid>
        </ProfileSection>

        <ProfileSection
          id="pricing-rtl"
          title="RTL / GUC pricing"
          desc="Annual % (0.25 = 0.25%). The tier sets the default account spread for every RTL and Ground Up product; a per-product override replaces it for that product, and the overlay adjusts up or down on top. Required coupon = required base rate (by term) + effective spread; the investor buy rate is the required base, below the coupon by the spread."
        >
          <Grid>
            <Cell span={6}>
              <PricingField label="RTL Buy Rate">
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={draft.rtlTier}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, rtlTier: e.target.value }))
                  }
                  SelectProps={{ displayEmpty: true }}
                  sx={FIELD_SX}
                >
                  {/* Blank = no RTL-specific tier: RTL prices on the
                      correspondent's tier (owner 2026-10-08). */}
                  <MenuItem value="" sx={{ fontSize: 13, color: PT.faint }}>
                    {fallbackTierLabel
                      ? `Same as correspondent tier (${fallbackTierLabel})`
                      : "— No tier —"}
                  </MenuItem>
                  {rtlTierOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </PricingField>
              {hint(
                rtlTiers === null ? (
                  "Loading tiers…"
                ) : rtlTiers === "error" ? (
                  "Couldn't load the tier table."
                ) : !rtlTierOptions.length ? (
                  "No tiers set up yet — add them in Admin › Pricing › RTL & GUC Pricing."
                ) : !effectiveRtlTier ? (
                  "No correspondent tier either — pick a tier to set the default spread."
                ) : selectedTierUnpriced ? (
                  `${effectiveRtlTier} tier spread: not yet priced by the desk.`
                ) : rtlTierFromCorrespondent ? (
                  <>
                    Using the correspondent's tier, {effectiveRtlTier}:{" "}
                    <b>{fmtPct(selectedTierSpread)}</b>. Pick a tier only if RTL
                    should differ.
                  </>
                ) : (
                  <>
                    Tier spread: <b>{fmtPct(selectedTierSpread)}</b>
                  </>
                ),
                selectedTierUnpriced,
              )}
            </Cell>
            <Cell span={6}>
              <PricingField label="Spread overlay (%)">
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  placeholder="0"
                  inputProps={{ step: 0.05 }}
                  value={draft.rtlOverlay}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, rtlOverlay: e.target.value }))
                  }
                  sx={FIELD_SX}
                />
              </PricingField>
              {hint(
                "Added on top of every product. May be negative (−0.10 = 0.10% off).",
              )}
            </Cell>
            <Cell span={12}>
              <PricingField
                label="Overlay note"
                required={rtlOverlayValue !== 0}
                helperText="Why the overlay is set. Required whenever the overlay isn't 0."
              >
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  inputRef={noteInputRef}
                  value={draft.rtlOverlayNote}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, rtlOverlayNote: e.target.value }))
                  }
                  placeholder='e.g. "month-end $5M volume incentive, review 10/1"'
                  error={rtlNoteMissing}
                  sx={FIELD_SX}
                />
              </PricingField>
              {rtlNoteMissing && (
                <Box
                  sx={{ ...FIELD_TEXT.helper, color: statusColors.error.fg }}
                >
                  A note is required while the overlay is set.
                </Box>
              )}
            </Cell>

            {rtlLegacyOnly && (
              <Cell span={12}>
                <Flx
                  g={0.75}
                  sx={{
                    alignItems: "flex-start",
                    borderRadius: "8px",
                    border: `1px solid ${PT.warnLine}`,
                    bgcolor: PT.warnSoft,
                    px: 1.5,
                    py: 1,
                    fontSize: 12.5,
                    color: PT.warn,
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 15, mt: "2px" }} />
                  No tier — neither an RTL tier nor a correspondent tier is set,
                  so this correspondent prices off its per-product spreads only.
                  Select a pricing tier so products without an override pick up
                  the tier spread.
                </Flx>
              </Cell>
            )}

            <Cell span={12}>
              <Flx
                ac
                jb
                sx={{
                  mt: 2,
                  pt: 3,
                  borderTop: `1px solid ${PT.lineSoft}`,
                }}
              >
                <Box sx={{ fontSize: 13, fontWeight: 600, color: PT.ink }}>
                  Per-product overrides (%)
                </Box>
                <Box sx={{ fontSize: 12, color: PT.muted }}>
                  {rtlOverrideCount
                    ? `${rtlOverrideCount} of ${orderedRtlProducts.length} overridden`
                    : "Blank = use the tier"}
                </Box>
              </Flx>
            </Cell>

            {orderedRtlProducts.map((p) => {
              const r = resolveRtlProduct(p.key);
              const effective =
                r.total != null
                  ? r.total
                  : r.base == null && rtlOverlayValue === 0
                    ? null
                    : (r.base || 0) + rtlOverlayValue;
              const tierWord = `${r.tierName || effectiveRtlTier} tier${
                r.fromCorrespondent ? " (correspondent's)" : ""
              }`;
              const source = {
                override: "override",
                legacy: "legacy spread",
                tier: tierWord,
                unpriced: `${tierWord}, not yet priced`,
                none: "no tier",
              }[r.basis];
              const flagged = r.basis !== "tier" && r.basis !== "none";
              return (
                <Cell key={p.key} span={3} spanSm={6}>
                  <PricingField
                    label={`${p.secondary_of ? "↳ " : ""}${p.label || p.key}`}
                  >
                    <TextField
                      type="number"
                      size="small"
                      fullWidth
                      placeholder={
                        effectiveRtlTier && selectedTierRow && !selectedTierUnpriced
                          ? `Tier ${trimZeros(selectedTierSpread.toFixed(3)) || "0"}`
                          : "—"
                      }
                      inputProps={{ step: 0.05 }}
                      value={draft.rtlSpreads?.[p.key] ?? ""}
                      onChange={(e) => setSpread(p.key, e.target.value)}
                      sx={FIELD_SX}
                    />
                  </PricingField>
                  {hint(
                    effective == null ? (
                      "No spread"
                    ) : (
                      <>
                        Effective <b>{fmtPct(effective)}</b> · {source}
                        {rtlOverlayValue !== 0 &&
                          ` ${fmtPct(rtlOverlayValue, { signed: true })} overlay`}
                      </>
                    ),
                    flagged,
                  )}
                </Cell>
              );
            })}
          </Grid>
        </ProfileSection>

        <ProfileSection
          id="pricing-lock"
          title="Lock configuration"
          desc="Caps how long this correspondent can lock and what extensions cost."
        >
          <Grid>
            <Cell span={6}>
              <PricingField
                label="Max lock days"
                helperText="Hides longer lock periods and caps extensions. Blank = no cap."
              >
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  placeholder="No cap"
                  inputProps={{ step: 1, min: 0 }}
                  value={draft.maxLockDays ?? ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, maxLockDays: e.target.value }))
                  }
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={6}>
              <PricingField
                label="Extension fee (bps / day)"
                helperText="Default 2.0. Fee = extension days × this, unless a preset matches."
              >
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ step: 0.5, min: 0 }}
                  value={draft.feeBpsPerDay ?? ""}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, feeBpsPerDay: e.target.value }))
                  }
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>

            <Cell span={12}>
              <Flx
                ac
                jb
                sx={{ pt: 1, borderTop: `1px solid ${PT.lineSoft}` }}
              >
                <Box sx={{ fontSize: 13, fontWeight: 600, color: PT.ink }}>
                  Extension fee presets
                </Box>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AddRounded sx={{ fontSize: 16 }} />}
                  onClick={addFeeOption}
                  sx={{ textTransform: "none" }}
                >
                  Add preset
                </Button>
              </Flx>
              {(draft.feeOptions || []).length === 0 ? (
                hint(
                  "No presets — the per-day rate above is used for every extension.",
                )
              ) : (
                <Stack spacing={1.5} sx={{ mt: 1 }}>
                  {(draft.feeOptions || []).map((o, i) => (
                    <Flx key={i} g={2} sx={{ alignItems: "flex-end" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <PricingField label="Days">
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            inputProps={{ step: 1, min: 0 }}
                            value={o.days}
                            onChange={(e) =>
                              setFeeOption(i, "days", e.target.value)
                            }
                            sx={FIELD_SX}
                          />
                        </PricingField>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <PricingField label="Fee (bps)">
                          <TextField
                            type="number"
                            size="small"
                            fullWidth
                            inputProps={{ step: 1, min: 0 }}
                            value={o.bps}
                            onChange={(e) =>
                              setFeeOption(i, "bps", e.target.value)
                            }
                            sx={FIELD_SX}
                          />
                        </PricingField>
                      </Box>
                      <Tooltip title="Remove preset">
                        <IconButton
                          aria-label="Remove preset"
                          onClick={() => removeFeeOption(i)}
                          sx={{ mb: "2px" }}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Flx>
                  ))}
                </Stack>
              )}
            </Cell>
          </Grid>
        </ProfileSection>

        <ProfileSection
          id="pricing-adjustments"
          title={`Price adjustments (${adjustments.length})`}
          desc="Correspondent-specific price adjustments, in bond points."
        >
          {adjustments.length ? (
            <Stack spacing={1}>
              {adjustments.map((a, i) => (
                <Flx
                  key={i}
                  jb
                  g={2}
                  sx={{
                    alignItems: "flex-start",
                    borderRadius: "8px",
                    border: `1px solid ${PT.line}`,
                    px: 2,
                    py: 1.25,
                  }}
                >
                  <Box>
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: PT.ink }}>
                      {a.priceAdjustmentType || "Adjustment"}
                    </Box>
                    <Box sx={{ fontSize: 12, color: PT.muted }}>
                      {a.priceAdjustmentDescription}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color:
                        a.priceAdjustmentAmount < 0
                          ? statusColors.success.fg
                          : PT.ink,
                    }}
                  >
                    {a.priceAdjustmentAmount > 0 ? "+" : ""}
                    {fmtPts(a.priceAdjustmentAmount)}
                  </Box>
                </Flx>
              ))}
            </Stack>
          ) : (
            <Box sx={{ fontSize: 13, color: PT.muted }}>
              No adjustments — pricing flows straight from the tier config.
            </Box>
          )}
        </ProfileSection>

        <ProfileSection
          id="pricing-audit"
          title={`Audit trail (${audit.length})`}
          desc="Every pricing edit is logged with who, when and why. The reason below is saved with each field you change in the next save."
        >
          <Grid>
            <Cell span={12}>
              <PricingField
                label="Reason for this change"
                required={hasRtl || hasDscr}
                helperText="Saved with every field you change. Required to save RTL or DSCR pricing."
              >
                <TextField
                  inputRef={reasonInputRef}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                  fullWidth
                  placeholder="e.g. Q3 volume commitment met"
                  error={reasonMissing}
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={12}>
              {audit.length ? (
                <Stack spacing={1.5}>
                  {audit
                    .slice()
                    .reverse()
                    .map((a, i) => (
                      <Box
                        key={i}
                        sx={{ borderLeft: `2px solid ${PT.line}`, pl: 1.5 }}
                      >
                        <Box
                          sx={{ fontSize: 12, fontWeight: 600, color: PT.ink2 }}
                        >
                          {a.field}
                        </Box>
                        <Box sx={{ fontSize: 12.5, color: PT.muted }}>
                          <Box
                            component="span"
                            sx={{
                              color: PT.faint,
                              textDecoration: "line-through",
                            }}
                          >
                            {auditValue(a.oldValue)}
                          </Box>
                          <Box component="span" sx={{ mx: 0.75, color: PT.faint }}>
                            →
                          </Box>
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: PT.ink }}
                          >
                            {auditValue(a.newValue)}
                          </Box>
                        </Box>
                        <Box sx={{ mt: 0.25, fontSize: 11.5, color: PT.faint }}>
                          {a.changedBy} · {fmtDate(a.changedDateTime)}
                          {a.reason ? ` · "${a.reason}"` : ""}
                        </Box>
                      </Box>
                    ))}
                </Stack>
              ) : (
                <Box sx={{ fontSize: 13, color: PT.muted }}>
                  No pricing changes recorded yet.
                </Box>
              )}
            </Cell>
          </Grid>
        </ProfileSection>
      </PricingPageLayout>

      <PendingChangesBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={cancelChanges}
        label="Save pricing"
        subText={
          rtlNoteMissing
            ? "Add an overlay note to save the RTL spread"
            : reasonMissing
              ? "Add a reason (Audit trail) to save RTL or DSCR pricing"
              : undefined
        }
      />
    </>
  );
}

// The "effective" result strip under the DSCR inputs — teal when pricing is
// the plain tier, amber when an override or extra margin moves it.
function EffectiveStrip({ label, detail, value, adjusted }) {
  return (
    <Flx
      ac
      jb
      sx={{
        borderRadius: "8px",
        border: `1px solid ${adjusted ? PT.warnLine : teal[100]}`,
        bgcolor: adjusted ? PT.warnSoft : teal[50],
        px: 2,
        py: 1.5,
      }}
    >
      <Box sx={{ fontSize: 12.5, color: PT.ink2 }}>
        <b>{label}</b>
        <Box
          component="span"
          sx={{ ml: 1, color: adjusted ? PT.warn : teal[700] }}
        >
          {detail}
        </Box>
      </Box>
      <Box
        sx={{
          fontSize: 17,
          fontWeight: 600,
          color: adjusted ? PT.warn : teal[700],
        }}
      >
        {value}
      </Box>
    </Flx>
  );
}

// Top of the Pricing tab: one line saying when pricing last changed, by whom,
// which fields and why (correspondent-admin-and-locks.md §"Audit").
function LastPricingChangeStrip({ detail }) {
  const lc = lastPricingChangeOf(detail);
  const fields = pricingChangeFields(lc);
  const reason = String(lc?.reason || "").trim();

  return (
    <Flx
      ac
      g={1.25}
      sx={{
        borderRadius: "12px",
        border: `1px solid ${PT.line}`,
        bgcolor: PT.surface,
        px: 2.5,
        py: 1.5,
        fontSize: 12.5,
        color: PT.muted,
        lineHeight: 1.5,
      }}
    >
      <HistoryRounded sx={{ fontSize: 18, color: PT.faint, flexShrink: 0 }} />
      {lc ? (
        <Box sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ fontWeight: 600, color: PT.ink }}>
            Last pricing change
          </Box>{" "}
          {formatDateTime(lc.at, "datetime") || "—"} ·{" "}
          <Box component="span" sx={{ color: PT.ink2, fontWeight: 500 }}>
            {lc.by || "unknown user"}
          </Box>
          {fields ? ` · ${fields}` : ""}
          {reason ? (
            <Box component="span" sx={{ color: PT.ink2 }}>
              {` — “${reason}”`}
            </Box>
          ) : (
            <Box component="span" sx={{ color: PT.faint }}>
              {" "}
              — no reason given
            </Box>
          )}
        </Box>
      ) : (
        <Box>No pricing changes recorded yet.</Box>
      )}
    </Flx>
  );
}

// Audit values are scalars, the per-product spread map, or the fee-preset
// list — render the structured ones readably instead of "[object Object]".
const auditValue = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

/* --------------------------------- Overlays -------------------------------- */

// GuidelineOverlay is keyed by `overlayId` (a plain string the backend mints on
// push) — NOT `_id`/`oid`, which is what the shared `docId` reads. Addressing an
// overlay with docId() yields "" and every write silently no-ops: the update and
// delete routes match on `guidelineOverlays.overlayId`, so nothing matches and a
// `modified: 0` comes back 200. Legacy keys stay as a fallback in case an older
// seeded row carries one.
const overlayIdOf = (o) => o?.overlayId ?? o?._id ?? o?.id ?? "";

function OverlaysTab({ detail, onChanged }) {
  const correspondentsApi = useCorrespondentsApi();
  const cid = docId(detail);
  // GET /correspondents/{id}/overlays is the authoritative list read; the copy
  // embedded on the company doc is the pre-fetch fallback.
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (!cid) return;
    correspondentsApi
      .listOverlays(cid)
      .then((r) => setFetched(r?.guidelineOverlays ?? []))
      .catch(() => setFetched(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, detail]);
  const overlays =
    fetched ?? detail.guidelineOverlays ?? detail.overlays ?? [];

  // null = closed; {} = the New-overlay form; an overlay object = editing it.
  // "New overlay" used to POST a blank named "{Company} Overlay" on the spot and
  // leave you with no way to fill it in, so it opens the same form instead.
  const [editing, setEditing] = useState(null);

  const removeOverlay = async (ov) => {
    const oid = overlayIdOf(ov);
    if (!oid) return;
    await correspondentsApi.deleteOverlay(cid, oid);
    onChanged();
  };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "13px",
            lineHeight: 1.6,
            color: T.ink500,
          }}
        >
          Overlays layer <b>additively</b> on top of base product guidelines —
          they never replace them. When a loan is created for this partner, each
          overlay emits extra underwriting requirements so underwriters can see
          the origin.
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => setEditing({})}
          sx={{ flexShrink: 0 }}
        >
          New overlay
        </Button>
      </Box>

      {overlays.length ? (
        overlays.map((o) => (
          <SectionCard
            key={overlayIdOf(o) || o.overlayName}
            title={o.overlayName || "Overlay"}
            icon={DescriptionOutlined}
            action={
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Edit overlay">
                  <IconButton
                    size="small"
                    aria-label="Edit overlay"
                    sx={{ color: T.ink300, "&:hover": { color: T.teal600 } }}
                    onClick={() => setEditing(o)}
                  >
                    <EditOutlined sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  aria-label="Delete overlay"
                  sx={{ color: T.ink300, "&:hover": { color: T.red } }}
                  onClick={() => removeOverlay(o)}
                >
                  <DeleteOutlineRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            }
          >
            {o.overlayDescription && (
              <Box
                sx={{
                  fontSize: 13,
                  color: T.ink700,
                  lineHeight: 1.6,
                  // Descriptions carry \n line breaks (and indentation) from the
                  // source notes — render them like a textarea, preserving
                  // newlines/spaces while still wrapping long lines.
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {o.overlayDescription}
              </Box>
            )}
            {(o.checkListItems ?? []).length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Label>Checklist items added to underwriting</Label>
                <Stack spacing={0.75}>
                  {o.checkListItems.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: 13,
                        color: T.ink700,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: T.teal600,
                        }}
                      />
                      {item}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            {o.referenceGuideline && (
              <Box sx={{ mt: 1.5, fontSize: "11.5px", color: T.ink300 }}>
                Reference: {o.referenceGuideline}
              </Box>
            )}
          </SectionCard>
        ))
      ) : (
        <Box
          sx={{
            borderRadius: "16px",
            border: `1px dashed ${T.ink300}`,
            background: "#fff",
            px: 3,
            py: 6,
            textAlign: "center",
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 26, color: T.ink300 }} />
          <Box sx={{ mt: 1, fontWeight: 600, color: T.ink700 }}>
            No overlays
          </Box>
          <Box sx={{ mt: 0.5, fontSize: 13, color: T.ink500 }}>
            This partner underwrites against base product guidelines only.
          </Box>
        </Box>
      )}

      {editing && (
        <CorrespondentOverlayPanel
          cid={cid}
          overlay={editing}
          overlayId={overlayIdOf(editing) || undefined}
          subtitle={detail.companyName}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </Stack>
  );
}

/* ------------------------------- Agreements -------------------------------- */

// Every correspondent document, laid out like the loan Documents tab's group
// coverage view: every category shown (empty ones too), agreements versioned.
// See components/CorrespondentDocumentsCoverage.js.
function AgreementsTab({ detail, onChanged }) {
  return <CorrespondentDocumentsCoverage detail={detail} onChanged={onChanged} />;
}

/* ---------------------------------- Notes ---------------------------------- */

// GET/POST /correspondents/{id}/editor — a versioned rich-text note on the
// correspondent (the backend calls it `editorNote`), NOT a config form. Each
// save snapshots the prior version into `versions[]` ({version, htmlString,
// json_content, updatedBy, updatedAt}). Clicking a version shows it read-only
// in the editor; "Restore" saves it back as a new version.
//
// Unsaved text survives leaving the tab (correspondent-loans-and-notes-tabs.md
// §2): the draft is kept in this browser under `corrNoteDraft:{id}` as
// { htmlString, blocks, baseVersion, savedAt } and put back when the tab opens,
// so switching tabs, closing the panel or navigating away never loses it. It's
// cleared on save / discard. A draft started on an older version than the one
// now saved says so. Closing or reloading the browser tab with a draft asks
// first. No autosave: every save is a new version and only the last 30 are kept.
const noteDraftKey = (cid) => `corrNoteDraft:${cid}`;
const readNoteDraft = (cid) => {
  try {
    return JSON.parse(partnerStorage.getItem(noteDraftKey(cid)) || "null");
  } catch {
    return null;
  }
};
const writeNoteDraft = (cid, draft) => {
  try {
    partnerStorage.setItem(noteDraftKey(cid), JSON.stringify(draft));
  } catch {
    // storage unavailable — the draft lives for this visit only
  }
};
const clearNoteDraft = (cid) => {
  try {
    partnerStorage.removeItem(noteDraftKey(cid));
  } catch {
    // nothing stored
  }
};

function NotesTab({ detail, onDirtyChange }) {
  const correspondentsApi = useCorrespondentsApi();
  const cid = docId(detail);
  const [note, setNote] = useState(null);
  const [draft, setDraft] = useState(null); // { htmlString, blocks }
  const [viewing, setViewing] = useState(null); // a versions[] entry, or null = current
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A restored draft started on an older version than the server's: the
  // server note it's behind, until the user keeps theirs or loads the newer.
  const [staleAgainst, setStaleAgainst] = useState(null);
  // The editor reports the loaded note back in its own html, which rarely
  // matches the stored html byte for byte. Its first report after a load is the
  // "saved" baseline; a restored draft has no baseline (it IS a change).
  const baselineRef = useRef(null);
  const restoredRef = useRef(false);

  const load = useCallback(() => {
    if (!cid) return;
    correspondentsApi
      .getEditor(cid)
      .then((r) => {
        const server = r || {};
        const stored = readNoteDraft(cid);
        const keep = stored && stored.htmlString !== (server.htmlString || "");
        baselineRef.current = null;
        restoredRef.current = !!keep;
        setNote(server);
        setDraft(keep ? { htmlString: stored.htmlString, blocks: stored.blocks } : null);
        setStaleAgainst(
          keep && Number(stored.baseVersion || 0) < Number(server.version || 0) ? server : null,
        );
        if (!keep) clearNoteDraft(cid);
        setViewing(null);
      })
      .catch((e) =>
        setError(e?.response?.data?.detail || e?.message || "Failed to load"),
      );
  }, [cid, correspondentsApi]);

  useEffect(load, [load]);

  const discard = () => {
    clearNoteDraft(cid);
    baselineRef.current = null;
    restoredRef.current = false;
    setStaleAgainst(null);
    setDraft(null);
  };

  const saveBody = async (body) => {
    setBusy(true);
    setError(null);
    try {
      await correspondentsApi.saveEditor(cid, body);
      clearNoteDraft(cid);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    saveBody({
      json_content: draft?.blocks ?? note?.json_content ?? null,
      htmlString: draft?.htmlString ?? note?.htmlString ?? "",
    });

  const restore = () =>
    saveBody({
      json_content: viewing?.json_content ?? null,
      htmlString: viewing?.htmlString ?? "",
      markdown_content: viewing?.markdown_content ?? null,
    });

  const currentHtml = note?.htmlString || "";
  // "Unsaved" means the html really differs from the saved note (as the editor
  // renders it), not that onChange fired.
  const dirty = !!draft && draft.htmlString !== (baselineRef.current ?? currentHtml);

  // Keep the draft in this browser while it differs; clear it once it doesn't.
  useEffect(() => {
    if (!cid || !note) return;
    if (dirty) {
      writeNoteDraft(cid, {
        htmlString: draft.htmlString,
        blocks: draft.blocks ?? null,
        baseVersion: note.version ?? 0,
        savedAt: new Date().toISOString(),
      });
    } else {
      clearNoteDraft(cid);
    }
  }, [cid, note, draft, dirty]);

  // The tab label shows the draft; closing / reloading the browser tab asks.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange && onDirtyChange(!!readNoteDraft(cid)), [cid, onDirtyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!note)
    return <Box sx={{ fontSize: 13, color: T.ink300 }}>Loading…</Box>;

  const history = note.versions ?? [];
  const editorValue = viewing
    ? viewing.htmlString || ""
    : (draft?.htmlString ?? currentHtml);

  // Current version first, then the snapshots newest-first.
  const rows = [
    ...(note.version
      ? [
          {
            key: "current",
            version: note.version,
            by: note.updatedBy,
            at: note.updatedAt,
            current: true,
          },
        ]
      : []),
    ...[...history].reverse().map((v, i) => ({
      key: `v-${v.version ?? i}`,
      version: v.version,
      by: v.updatedBy || v.savedBy,
      at: v.updatedAt || v.savedDate,
      snapshot: v,
    })),
  ];

  return (
    <FlxGrid width="520px" gap={2.5}>
      <SectionCard
        title={
          viewing
            ? `Viewing v${viewing.version ?? "—"} · read only`
            : `Correspondent note${note.version ? ` · v${note.version}` : ""}`
        }
        icon={NotesRounded}
        action={
          viewing ? (
            <Flx ac g={0.5}>
              <Button
                variant="text"
                size="small"
                onClick={() => setViewing(null)}
                sx={{ fontWeight: 600, fontSize: "12px" }}
              >
                Back to current
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={busy}
                onClick={restore}
                sx={{ fontWeight: 600, fontSize: "12px" }}
              >
                {busy ? "Restoring…" : "Restore this version"}
              </Button>
            </Flx>
          ) : (
            <Flx ac g={0.5}>
              {dirty ? (
                <Button
                  variant="text"
                  size="small"
                  disabled={busy}
                  onClick={discard}
                  sx={{ fontWeight: 600, fontSize: "12px" }}
                >
                  Discard
                </Button>
              ) : null}
              <Button
                variant="text"
                size="small"
                disabled={!dirty || busy}
                onClick={save}
                sx={{ fontWeight: 600, fontSize: "12px" }}
              >
                {busy ? "Saving…" : dirty ? "Save" : "Saved"}
              </Button>
            </Flx>
          )
        }
      >
        {!viewing && dirty ? (
          <Alert severity={staleAgainst ? "warning" : "info"} sx={{ mb: 1.5, fontSize: 12.5 }}>
            {staleAgainst ? (
              <>
                A newer version was saved by {staleAgainst.updatedBy || "someone"} on{" "}
                {formatDateTime(staleAgainst.updatedAt, "datetime") || "—"} after you started this
                draft.{" "}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setStaleAgainst(null)}
                  sx={{ p: 0, border: 0, bgcolor: "transparent", font: "inherit", fontWeight: 600, color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                >
                  Keep mine
                </Box>{" "}
                ·{" "}
                <Box
                  component="button"
                  type="button"
                  onClick={discard}
                  sx={{ p: 0, border: 0, bgcolor: "transparent", font: "inherit", fontWeight: 600, color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                >
                  Load the newer one
                </Box>
              </>
            ) : (
              "Unsaved changes. They're kept on this computer until you save or discard them."
            )}
          </Alert>
        ) : null}
        <BlockNoteEditor
          value={editorValue}
          readOnly={!!viewing}
          onChange={({ htmlString, blocks }) => {
            if (viewing) return;
            // The editor's first report of the saved note is the baseline.
            if (baselineRef.current === null && !restoredRef.current) {
              baselineRef.current = htmlString;
              return;
            }
            setDraft({ htmlString, blocks });
          }}
          minHeight="260px"
          maxHeight="520px"
          placeholder="Partner-specific instructions, exceptions, escalation paths…"
        />
        {error && (
          <Box sx={{ mt: 1, fontSize: 12.5, color: statusColors.error.fg }}>
            {error}
          </Box>
        )}
      </SectionCard>

      <SectionCard
        title={`Version history (${rows.length})`}
        icon={DescriptionOutlined}
      >
        {rows.length ? (
          <Stack spacing={1}>
            {rows.map((r) => {
              const active = r.current
                ? !viewing
                : viewing === r.snapshot;
              return (
                <Box
                  key={r.key}
                  component="button"
                  type="button"
                  onClick={() => setViewing(r.current ? null : r.snapshot)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    font: "inherit",
                    borderRadius: "8px",
                    border: `1px solid ${active ? T.teal600 : T.line}`,
                    background: active ? T.teal50 : "#fff",
                    px: 1.5,
                    py: 1,
                    fontSize: "12.5px",
                    "&:hover": { borderColor: T.teal600 },
                  }}
                >
                  <Flx ac g={1}>
                    <Box
                      sx={{
                        fontWeight: 600,
                        color: active ? T.teal600 : T.ink900,
                      }}
                    >
                      v{r.version ?? "—"}
                    </Box>
                    {r.current && <SoftBadge tone="green">Current</SoftBadge>}
                  </Flx>
                  <Box sx={{ color: T.ink300 }}>
                    {r.by || "—"} · {formatDateTime(r.at, "datetime") || "—"}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ fontSize: 13, color: T.ink300 }}>
            No versions yet — the first save creates one.
          </Box>
        )}
      </SectionCard>
    </FlxGrid>
  );
}

/* ---------------------------------- Users ---------------------------------- */

function UsersTab({ cid, detail }) {
  const correspondentsApi = useCorrespondentsApi();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const domains = detail.companyEmailDomains ?? [];

  // There is no per-correspondent user route — the hook resolves the roster out
  // of /users/all_users by the pinned `correspondentCompanyId`, falling back to
  // the company's email domains for users created before that pin existed.
  const refresh = () => {
    correspondentsApi
      .listUsers(cid, { emailDomains: domains })
      .then((r) => setUsers(Array.isArray(r) ? r : []))
      .catch(() => setUsers([]));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, domains.join(",")]);

  const invite = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await correspondentsApi.createUser(cid, {
        emailAddress: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
      });
      setForm({ email: "", firstName: "", lastName: "" });
      refresh();
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Failed to invite user",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        gridTemplateColumns: { xs: "1fr", lg: "1fr 360px" },
      }}
    >
      <SectionCard title={`Portal users (${users.length})`} icon={MailOutline}>
        {users.length ? (
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${T.line}` }} />}>
            {users.map((u) => {
              const name =
                u.full_name || contactName(u) || u.email || u.emailAddress;
              const email = u.email || u.emailAddress;
              const active = u.is_active ?? u.status === "Active";
              return (
                <Box
                  key={email}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    py: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: T.teal50,
                      color: T.teal600,
                      display: "grid",
                      placeItems: "center",
                      fontSize: "11px",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {contactInitials({ firstName: name })}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{ fontSize: 13, fontWeight: 600, color: T.ink700 }}
                    >
                      {name}
                    </Box>
                    <Box sx={{ fontSize: 12, color: T.ink300 }}>{email}</Box>
                  </Box>
                  <SoftBadge tone={active ? "green" : "slate"}>
                    {active ? "Active" : "Invited"}
                  </SoftBadge>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ fontSize: 13, color: T.ink300 }}>
            No portal users yet — invite one to give this partner access.
          </Box>
        )}
      </SectionCard>

      <SectionCard title="Invite user" icon={AddRounded}>
        <Stack spacing={1.5}>
          <Box>
            <Label>Email address</Label>
            <TextField
              size="small"
              fullWidth
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              placeholder={
                domains[0] ? `name@${domains[0]}` : "name@company.com"
              }
              sx={inputSx}
            />
          </Box>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            <Box>
              <Label>First name</Label>
              <TextField
                size="small"
                fullWidth
                value={form.firstName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, firstName: e.target.value }))
                }
                sx={inputSx}
              />
            </Box>
            <Box>
              <Label>Last name</Label>
              <TextField
                size="small"
                fullWidth
                value={form.lastName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, lastName: e.target.value }))
                }
                sx={inputSx}
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            disabled={submitting || !form.email.trim()}
            onClick={invite}
          >
            {submitting ? "Sending…" : "Send invite"}
          </Button>
          {error && <Box sx={{ fontSize: 12, color: T.red }}>{error}</Box>}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              fontSize: "11.5px",
              lineHeight: 1.6,
              color: T.ink300,
            }}
          >
            <InfoOutlined sx={{ fontSize: 13, mt: "1px" }} />
            Signups are domain-gated: the email's domain must be one of this
            correspondent's registered domains
            {domains.length
              ? ` (${domains.map((d) => `@${d}`).join(", ")})`
              : ""}
            .
          </Box>
        </Stack>
      </SectionCard>
    </Box>
  );
}

/* ---------------------------------- Loans ---------------------------------- */

// Rows + loading come from CorrespondentDetailPanel — it already resolves them
// out of the shared cached pipeline so the tab label can show the count.
// The Loans tab is a pipeline REVIEW surface (stat strip + volume-by-month +
// outcome/product mix + the filtered table), not a bare list — it lives in its
// own file because it carries the chart code. Rows + loading come from
// CorrespondentDetailPanel, which already resolves them out of the shared cached
// pipeline so the tab label can show the count.
function LoansTab({ detail, rows = [], loading = false }) {
  return (
    <CorrespondentLoansTab detail={detail} rows={rows} loading={loading} />
  );
}
